import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { video as videoApi, attempts as attemptsApi, getApiBaseUrl } from '../../../services/api';
import { TranscriptTurn } from '../../evaluation/types';
import {
  speakSpeechSynthesis,
  stopAllSpeech,
  pauseSpeech,
  preloadVoices,
  claimPlayback,
  releasePlayback,
  isFemaleName,
} from '../../../services/voiceService';
import { SpeechControlledAvatar, getPersonaAvatarSVG } from '../../simulation/components/SpeechControlledAvatar';
import { getSessionRecordingBlob, getTurnAudioBlob } from '../utils/mediaClientStorage';

interface AssessmentRecordingReplayCardProps {
  attemptId: string;
  videoUrl?: string;
  duration?: string | number;
  simulationTitle?: string;
  learnerName?: string;
  recordedAt?: string;
  transcript?: TranscriptTurn[];
  personaName?: string;
  personaAvatarUrl?: string;
  personaAvatarType?: string;
  avatarType?: string;
  personaRole?: string;
  personaMood?: string;
  genderPreference?: string;
  ttsVoice?: string;
  country?: string;
  tone?: string;
  recordingStatus?: 'READY' | 'PROCESSING' | 'RECORDING' | 'UPLOADED' | 'FAILED' | 'UNAVAILABLE' | string;
  captureMode?: 'AUDIO_ONLY' | 'VIDEO' | 'AUDIO_VIDEO' | string;
  mimeType?: string;
  errorReason?: string;
}

const normalizeMediaUrl = (url?: string | null): string | null => {
  if (!url) return null;
  const apiBase = getApiBaseUrl();
  // If the URL contains mock-storage with an external or obsolete host, rewrite it to current backend apiBase
  if (url.includes('/api/v1/mock-storage/')) {
    const mockStoragePath = url.substring(url.indexOf('/api/v1/mock-storage/'));
    return `${apiBase}${mockStoragePath}`;
  }
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) {
    return url;
  }
  if (url.startsWith('/')) {
    return `${apiBase}${url}`;
  }
  return url;
};

export const AssessmentRecordingReplayCard: React.FC<AssessmentRecordingReplayCardProps> = ({
  attemptId,
  videoUrl: initialVideoUrl,
  duration: initialDuration,
  simulationTitle,
  learnerName = 'Learner',
  recordedAt,
  transcript = [],
  personaName = 'AI Customer',
  personaAvatarUrl,
  personaAvatarType,
  avatarType,
  personaRole,
  personaMood,
  genderPreference,
  ttsVoice,
  country,
  tone,
  recordingStatus: initialRecordingStatus,
  captureMode: initialCaptureMode,
  mimeType: initialMimeType,
  errorReason: initialErrorReason,
}) => {
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(normalizeMediaUrl(initialVideoUrl));

  // AUD-FE-002: Track active object URLs and revoke them on unmount to prevent memory leaks
  const activeBlobUrlsRef = useRef<Set<string>>(new Set());
  const registerBlobUrl = useCallback((url: string): string => {
    if (url && url.startsWith('blob:')) {
      activeBlobUrlsRef.current.add(url);
    }
    return url;
  }, []);

  useEffect(() => {
    return () => {
      activeBlobUrlsRef.current.forEach((url) => {
        try { URL.revokeObjectURL(url); } catch {}
      });
      activeBlobUrlsRef.current.clear();
    };
  }, []);
  const [loading, setLoading] = useState<boolean>(!initialVideoUrl);
  const [recordingStatus, setRecordingStatus] = useState<string>(
    initialRecordingStatus || (initialVideoUrl ? 'READY' : 'PROCESSING')
  );
  const [errorReason, setErrorReason] = useState<string | null>(initialErrorReason || null);
  const [captureMode, setCaptureMode] = useState<string>(
    initialCaptureMode || (initialMimeType?.startsWith('audio/') ? 'AUDIO_ONLY' : 'AUDIO_VIDEO')
  );
  const [mimeType, setMimeType] = useState<string>(initialMimeType || 'video/webm');
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const currentTimeRef = useRef<number>(0);
  currentTimeRef.current = currentTime;
  const [durationSeconds, setDurationSeconds] = useState<number>(0);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isLooping, setIsLooping] = useState<boolean>(true);
  const isLoopingRef = useRef<boolean>(true);
  isLoopingRef.current = isLooping;

  const [storageProvider, setStorageProvider] = useState<string>('cloudinary');
  const [activeViewMode, setActiveViewMode] = useState<'MEET_GRID' | 'RAW_VIDEO'>('MEET_GRID');
  const [currentSpeaker, setCurrentSpeaker] = useState<'persona' | 'learner' | null>(null);
  const [currentSubtitle, setCurrentSubtitle] = useState<string | null>(null);
  const [isFullPage, setIsFullPage] = useState<boolean>(false);

  // Responsive viewport tracking for mobile & PWA full-screen replay
  const [isMobileScreen, setIsMobileScreen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 960;
  });
  const [isLandscape, setIsLandscape] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth > window.innerHeight && window.innerWidth < 960;
  });
  const [mobileFsView, setMobileFsView] = useState<'split' | 'video' | 'transcript'>('split');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setIsMobileScreen(w < 960);
      setIsLandscape(w > h && w < 960);
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  // 'SESSION' plays the single continuous recording captured during the live simulation
  // (learner mic + persona voice mixed together) with the transcript following the audio.
  // 'SYNTH' provides per-turn playback.
  const [playbackEngine, setPlaybackEngine] = useState<'SESSION' | 'SYNTH'>('SESSION');
  const [mediaDuration, setMediaDuration] = useState<number>(0);

  const [activeTurnIndex, setActiveTurnIndex] = useState<number>(0);
  const activeTurnIndexRef = useRef<number>(0);
  activeTurnIndexRef.current = activeTurnIndex;

  const [videoHasError, setVideoHasError] = useState<boolean>(false);
  const [hasNoVideoTracks, setHasNoVideoTracks] = useState<boolean>(false);
  const currentSpeakingAiTurnIdRef = useRef<string | null>(null);
  const lastPlayedTurnIdRef = useRef<string | null>(null);

  // Synchronous generation lock: guarantees cancellation of previous playback and prevents overlapping voices
  const playbackIdRef = useRef<number>(0);
  const isSegmentModeRef = useRef<boolean>(false);
  const segmentTimeoutRef = useRef<any>(null);
  const segmentEndSecRef = useRef<number | null>(null);

  // Keyboard shortcut: Esc to exit full page
  useEffect(() => {
    if (!isFullPage) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsFullPage(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isFullPage]);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const audioMediaRef = useRef<HTMLAudioElement | null>(null);
  const activeTurnRef = useRef<HTMLDivElement | null>(null);
  const transcriptContainerRef = useRef<HTMLDivElement | null>(null);
  const lastScrolledTurnIdxRef = useRef<number | null>(null);
  const playTimerRef = useRef<any>(null);
  const activeSpeechCancelRef = useRef<(() => void) | null>(null);
  const activeAudioElementRef = useRef<HTMLAudioElement | null>(null);
  const isAudioPlayingRef = useRef<boolean>(false);
  const isPlayingRef = useRef<boolean>(false);
  const currentTurnIdxRef = useRef<number>(0);
  const learnerAudioTimeoutRef = useRef<any>(null);
  const playTurnAtIndexRef = useRef<((idx: number) => void) | null>(null);

  // Web Audio API references
  const audioContextRef = useRef<AudioContext | null>(null);
  const activeBufferSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);

  // Normalize duration to total seconds
  const totalDurationSec = useMemo(() => {
    if (mediaDuration > 0 && isFinite(mediaDuration)) return Math.round(mediaDuration);
    if (durationSeconds > 0) return durationSeconds;
    if (typeof initialDuration === 'number' && initialDuration > 0) return initialDuration;
    if (typeof initialDuration === 'string' && initialDuration.includes(':')) {
      const parts = initialDuration.split(':');
      const m = parseInt(parts[0], 10);
      const s = parseInt(parts[1], 10);
      if (!isNaN(m) && !isNaN(s) && (m * 60 + s) > 0) return m * 60 + s;
    }
    if (transcript && transcript.length > 0) return Math.max(60, transcript.length * 20);
    return 180;
  }, [mediaDuration, durationSeconds, initialDuration, transcript]);

  // Clean transcript turns with robust speaker attribution and verbatim spoken text extraction
  const cleanTurns = useMemo(() => {
    if (!transcript || transcript.length === 0) return [];

    // Session base start: prefer recordedAt, else the first turn's timestamp
    let sessionStartMs = 0;
    if (recordedAt && !isNaN(Date.parse(recordedAt))) {
      sessionStartMs = new Date(recordedAt).getTime();
    } else {
      const firstTurnTs = transcript[0]?.timestamp;
      if (typeof firstTurnTs === 'number' && firstTurnTs > 1e11) {
        sessionStartMs = firstTurnTs;
      } else if (typeof firstTurnTs === 'string' && !isNaN(Date.parse(firstTurnTs))) {
        sessionStartMs = new Date(firstTurnTs).getTime();
      }
    }

    let lastTurnSec = 0;
    let lastAudioDurationSec = 0;

    const mapped = transcript.map((t, idx) => {
      // 1. Robust Speaker Attribution (check explicit AI first to prevent misattribution)
      const spkRole = (((t as any).role || (t as any).speakerRole || '') + '').toLowerCase();
      const spk = ((t.speaker || '') + '').toUpperCase();
      const spkName = (((t as any).speakerName || '') + '').toLowerCase();
      const hasSpeechMetrics = Boolean((t as any).speechMetrics || (t as any).sttConfidence !== undefined);

      const isExplicitAi =
        spkRole === 'assistant' ||
        spkRole === 'persona' ||
        spkRole === 'ai' ||
        spkRole === 'system' ||
        spk === 'AI' ||
        spk === 'PERSONA' ||
        spk === 'ASSISTANT' ||
        spk === 'CUSTOMER' ||
        spkName.includes('persona') ||
        spkName.includes('assistant') ||
        spkName.includes('ai customer') ||
        Boolean(personaName && spkName.includes(personaName.toLowerCase()));

      const isExplicitLearner =
        spkRole === 'user' ||
        spkRole === 'learner' ||
        spk === 'LEARNER' ||
        spk === 'USER' ||
        spk === 'YOU' ||
        spkName.includes('learner') ||
        spkName.includes('you') ||
        Boolean(learnerName && learnerName !== 'Learner' && spkName.includes(learnerName.toLowerCase()));

      const isLearner = !isExplicitAi && (isExplicitLearner || hasSpeechMetrics);

      // 2. Verbatim Dialogue Text Extraction
      let rawText = t.text || (t as any).transcript || (t as any).content || (t as any).message || (t as any).spokenText || '';
      let text = rawText
        .replace(/^(CUSTOMER|LEARNER|AI|USER|PERSONA)\s*(\([^)]*\))?\s*(SAID|SAYS)?\s*:\s*/i, '')
        .replace(/^\[(CUSTOMER|LEARNER|AI|USER|PERSONA|[^\]]+)\]\s*:\s*/i, '')
        .replace(/^["']|["']$/g, '')
        .trim();

      // Estimated or actual audio duration for this turn
      let audioDurationSec = typeof (t as any).audioDurationSec === 'number'
        ? (t as any).audioDurationSec
        : typeof (t as any).durationSeconds === 'number'
          ? (t as any).durationSeconds
          : typeof (t as any).durationMs === 'number'
            ? Math.max(2, Math.round((t as any).durationMs / 1000))
            : Math.max(3, Math.round(text.split(' ').length / 2.5));

      // 3. Accurate Timestamp & Second Offset
      let turnSec = 0;
      if (typeof (t as any).turnSec === 'number' && (t as any).turnSec < 100000) {
        turnSec = Math.max(0, (t as any).turnSec);
      } else if (typeof (t as any).elapsedSeconds === 'number' && (t as any).elapsedSeconds < 100000) {
        turnSec = Math.max(0, (t as any).elapsedSeconds);
      } else if (typeof (t as any).audioStartSec === 'number' && (t as any).audioStartSec < 100000) {
        turnSec = Math.max(0, (t as any).audioStartSec);
      } else if (idx === 0) {
        turnSec = 0;
      } else {
        const speechStartMs = (t as any).speechMetrics?.speechStartMs;
        let turnTimeMs: number | null = null;
        if (typeof speechStartMs === 'number' && speechStartMs > 1e11) {
          turnTimeMs = speechStartMs;
        } else if (typeof t.timestamp === 'number' && t.timestamp > 1e11) {
          turnTimeMs = t.timestamp;
        } else if (typeof t.timestamp === 'string' && !isNaN(Date.parse(t.timestamp))) {
          turnTimeMs = new Date(t.timestamp).getTime();
        }

        const prevTurnEndSec = lastTurnSec + (lastAudioDurationSec || 4);

        if (turnTimeMs && sessionStartMs > 0) {
          const deltaSec = Math.max(0, Math.round((turnTimeMs - sessionStartMs) / 1000));
          if (!isLearner && deltaSec > prevTurnEndSec + 3) {
            // Live simulation AI speech starts shortly after learner utterance completion (~1.5-2s);
            // anchor artificial offline backend generation latency to synchronize dialogue display with spoken audio.
            turnSec = prevTurnEndSec + 2;
          } else {
            turnSec = deltaSec;
          }
        } else {
          turnSec = prevTurnEndSec + 1;
        }
      }

      // Preserve chronological monotonicity
      if (idx > 0 && turnSec < lastTurnSec) {
        turnSec = lastTurnSec;
      }
      lastTurnSec = turnSec;
      lastAudioDurationSec = audioDurationSec;

      const audioStartSec = typeof (t as any).audioStartSec === 'number' ? (t as any).audioStartSec : turnSec;
      const audioUrl = (t as any).audioUrl || (t as any).audio_url || (t as any).recordingUrl || '';
      const turnNumber = (t as any).turnNumber ?? ((t as any).turnIndex !== undefined ? (t as any).turnIndex + 1 : idx + 1);
      const turnId = (t as any).id || (t as any).turnId || `turn-${turnNumber}`;

      const rawAiSpeaker = (t as any).personaName || (t as any).speakerName || personaName || 'AI Customer';
      const cleanAiSpeaker = rawAiSpeaker.split('(')[0].trim() || rawAiSpeaker;

      const speakerName = isLearner
        ? (learnerName && learnerName !== 'Learner' ? learnerName : ((t as any).speakerName || learnerName || 'Learner'))
        : cleanAiSpeaker;

      return {
        id: turnId,
        turnNumber,
        isLearner,
        role: isLearner ? ('learner' as const) : ('persona' as const),
        speakerName,
        text,
        turnSec,
        audioStartSec,
        audioDurationSec,
        audioUrl,
        ttsVoice: (t as any).ttsVoice,
        ttsProvider: (t as any).ttsProvider,
        pitchPreference: (t as any).pitchPreference,
        speakingPace: (t as any).speakingPace,
        country: (t as any).country,
        tone: (t as any).tone,
        genderPreference: (t as any).genderPreference,
      };
    }).filter(t => Boolean(t.text && t.text.trim()));

    return mapped.map((turn, cleanIdx) => ({
      ...turn,
      originalIndex: cleanIdx,
    }));
  }, [transcript, learnerName, personaName, recordedAt]);

  // Determine if media is strictly audio-only.
  // Video container formats (.webm/.mp4) or video MIME types contain video tracks and must render the video element.
  const hasVideoMime = Boolean(mimeType?.startsWith('video/'));
  const hasVideoExtension = Boolean(
    resolvedUrl && (
      resolvedUrl.includes('.webm') ||
      resolvedUrl.includes('.mp4') ||
      resolvedUrl.includes('.mov') ||
      resolvedUrl.includes('/video/')
    )
  );
  const hasVideoCapability = hasVideoMime || hasVideoExtension;
  const isExplicitAudio = Boolean(
    (!hasVideoCapability && mimeType?.startsWith('audio/')) ||
    mimeType === 'audio/mpeg' ||
    mimeType === 'audio/mp3' ||
    mimeType === 'audio/wav' ||
    mimeType === 'audio/wave' ||
    mimeType === 'audio/aac' ||
    resolvedUrl?.endsWith('.mp3') ||
    resolvedUrl?.endsWith('.wav') ||
    resolvedUrl?.endsWith('.m4a') ||
    resolvedUrl?.endsWith('.aac')
  );
  const isAudioOnly = hasNoVideoTracks || isExplicitAudio || (!hasVideoCapability && captureMode === 'AUDIO_ONLY');

  // ---------------------------------------------------------------
  // Full-session playback engine
  // ---------------------------------------------------------------
  const sessionEngineAvailable = Boolean(resolvedUrl) && !videoHasError;
  const useSessionEngine = playbackEngine === 'SESSION' && sessionEngineAvailable;

  const sessionScale = useMemo(() => {
    // Media timeline is 1:1 with transcript turn seconds. Avoid scaling distortion.
    return 1;
  }, []);

  /** The media element that owns playback for the current view / capture mode. */
  const getSessionMedia = useCallback((): HTMLMediaElement | null => {
    if (!isAudioOnly && !videoHasError && videoRef.current) return videoRef.current;
    return audioMediaRef.current;
  }, [isAudioOnly, videoHasError]);

  /** Highlight the transcript turn covering a transcript-seconds timestamp. */
  const syncTranscriptToTime = useCallback((transcriptSec: number) => {
    if (cleanTurns.length === 0) return;
    let idx = cleanTurns.findIndex((t, i) =>
      transcriptSec >= t.turnSec && (i === cleanTurns.length - 1 || transcriptSec < cleanTurns[i + 1].turnSec)
    );
    if (idx === -1) {
      idx = transcriptSec < cleanTurns[0].turnSec ? 0 : cleanTurns.length - 1;
    }
    const turn = cleanTurns[idx];
    if (!turn) return;
    if (activeTurnIndexRef.current !== idx) {
      setActiveTurnIndex(idx);
      activeTurnIndexRef.current = idx;
      currentTurnIdxRef.current = idx;
      setCurrentSpeaker(turn.role);
      setCurrentSubtitle(turn.text);
    }
  }, [cleanTurns]);

  /** Pause all active media elements */
  const pauseSessionRecording = useCallback(() => {
    [videoRef.current, audioMediaRef.current].forEach((el) => {
      if (el && !el.paused) {
        try { el.pause(); } catch {}
      }
    });
  }, []);

  /** Stop all speech, audio buffers, media and timers synchronously */
  const stopAudioPlayback = useCallback(() => {
    if (segmentTimeoutRef.current) {
      clearTimeout(segmentTimeoutRef.current);
      segmentTimeoutRef.current = null;
    }
    if (learnerAudioTimeoutRef.current) {
      clearTimeout(learnerAudioTimeoutRef.current);
      learnerAudioTimeoutRef.current = null;
    }
    if (playTimerRef.current) {
      clearInterval(playTimerRef.current);
      playTimerRef.current = null;
    }
    if (videoRef.current) {
      try {
        videoRef.current.pause();
      } catch {}
    }
    if (audioMediaRef.current) {
      try {
        audioMediaRef.current.pause();
      } catch {}
    }
    if (activeBufferSourceRef.current) {
      try {
        activeBufferSourceRef.current.onended = null;
        activeBufferSourceRef.current.stop();
      } catch {}
      activeBufferSourceRef.current = null;
    }
    if (activeAudioElementRef.current) {
      try {
        activeAudioElementRef.current.onended = null;
        activeAudioElementRef.current.onerror = null;
        activeAudioElementRef.current.pause();
        activeAudioElementRef.current.src = '';
      } catch {}
      activeAudioElementRef.current = null;
    }
    if (activeSpeechCancelRef.current) {
      try {
        activeSpeechCancelRef.current();
      } catch {}
      activeSpeechCancelRef.current = null;
    }
    stopAllSpeech();
    releasePlayback('assessment-recording-card');
    isAudioPlayingRef.current = false;
    segmentEndSecRef.current = null;
  }, []);

  /** Smooth looping or completion handler when media stream ends */
  const handleMediaEnded = useCallback(() => {
    if (isSegmentModeRef.current) {
      if (segmentTimeoutRef.current) {
        clearTimeout(segmentTimeoutRef.current);
        segmentTimeoutRef.current = null;
      }
      setIsPlaying(false);
      isPlayingRef.current = false;
      isSegmentModeRef.current = false;
      segmentEndSecRef.current = null;
      return;
    }

    if (isLoopingRef.current) {
      const el = getSessionMedia();
      if (el) {
        try {
          el.currentTime = 0;
          setCurrentTime(0);
          syncTranscriptToTime(0);
          el.play().catch(() => {});
          return;
        } catch {}
      }
    }

    setIsPlaying(false);
    isPlayingRef.current = false;
    if (useSessionEngine) pauseSessionRecording();
  }, [getSessionMedia, pauseSessionRecording, syncTranscriptToTime, useSessionEngine]);

  /** Media timeupdate bridge: keep the scrubber, subtitle, transcript and segment limits in sync */
  const handleSessionTimeUpdate = useCallback(() => {
    const el = getSessionMedia();
    if (!el || !useSessionEngine) return;

    const rawSec = el.currentTime / (sessionScale || 1);
    const mapped = Math.min(totalDurationSec, Math.round(rawSec));
    if (currentTimeRef.current !== mapped) {
      currentTimeRef.current = mapped;
      setCurrentTime(mapped);
    }
    syncTranscriptToTime(mapped);

    // Segment mode boundary enforcement: stop immediately when segment completes
    if (isSegmentModeRef.current && segmentEndSecRef.current !== null) {
      if (rawSec >= segmentEndSecRef.current || mapped >= segmentEndSecRef.current) {
        if (segmentTimeoutRef.current) {
          clearTimeout(segmentTimeoutRef.current);
          segmentTimeoutRef.current = null;
        }
        try { el.pause(); } catch {}
        setIsPlaying(false);
        isPlayingRef.current = false;
        isSegmentModeRef.current = false;
        segmentEndSecRef.current = null;
        return;
      }
    }

    // Full session continuous recording reached end: smoothly loop if loop is enabled
    if (!isSegmentModeRef.current && (rawSec >= totalDurationSec || mapped >= totalDurationSec)) {
      if (isLoopingRef.current) {
        try {
          el.currentTime = 0;
          setCurrentTime(0);
          syncTranscriptToTime(0);
          el.play().catch(() => {});
        } catch {}
      } else {
        try { el.pause(); } catch {}
        setIsPlaying(false);
        isPlayingRef.current = false;
      }
    }
  }, [getSessionMedia, sessionScale, syncTranscriptToTime, totalDurationSec, useSessionEngine]);

  const registerSessionMetadata = useCallback((el: HTMLMediaElement) => {
    const dur = el.duration;
    if (dur && !isNaN(dur) && isFinite(dur) && dur > 0) {
      setMediaDuration(dur);
      setDurationSeconds(Math.round(dur));
    }
    if (el instanceof HTMLVideoElement && el.videoWidth === 0 && el.videoHeight === 0) {
      setHasNoVideoTracks(true);
    }
  }, []);

  const playSessionRecording = useCallback((fromTranscriptSec?: number) => {
    const el = getSessionMedia();
    if (!el) return false;
    try {
      el.playbackRate = playbackSpeed;
      el.muted = isMuted;
      el.volume = isMuted ? 0 : 1;
      const maxDur = (el.duration && isFinite(el.duration) && el.duration > 0) ? el.duration : totalDurationSec;
      const target = Math.max(0, Math.min(maxDur, (fromTranscriptSec ?? currentTime) * (sessionScale || 1)));
      if (isFinite(target)) el.currentTime = target;
    } catch {}

    // Ensure secondary audio element is paused to avoid duplicate audio
    if (el === videoRef.current && audioMediaRef.current) {
      try { audioMediaRef.current.pause(); } catch {}
    } else if (el === audioMediaRef.current && videoRef.current) {
      try { videoRef.current.pause(); } catch {}
    }

    el.play().catch((playErr) => {
      console.warn('[AssessmentRecordingReplayCard] Session recording play rejected, falling back to turn replay:', playErr);
      setPlaybackEngine('SYNTH');
      if (playTurnAtIndexRef.current) {
        playTurnAtIndexRef.current(activeTurnIndexRef.current >= 0 ? activeTurnIndexRef.current : 0);
      } else {
        setIsPlaying(false);
        isPlayingRef.current = false;
        isSegmentModeRef.current = false;
      }
    });
    return true;
  }, [currentTime, getSessionMedia, isMuted, playbackSpeed, sessionScale, totalDurationSec]);

  const [transcriptSearch, setTranscriptSearch] = useState<string>('');

  const displayedTurns = useMemo(() => {
    if (!transcriptSearch.trim()) return cleanTurns;
    const q = transcriptSearch.toLowerCase().trim();
    return cleanTurns.filter(t => t.text.toLowerCase().includes(q) || t.speakerName.toLowerCase().includes(q));
  }, [cleanTurns, transcriptSearch]);

  const checkStatus = async () => {
    if (!attemptId) return;
    try {
      setLoading(true);
      const res = await videoApi.getRecording(attemptId);
      if (res.success && res.data) {
        const streamUrl = normalizeMediaUrl(res.data.playbackUrl || res.data.downloadUrl || res.data.publicUrl);
        if (streamUrl) {
          setResolvedUrl(streamUrl);
          setRecordingStatus('READY');
        } else if (res.data.status) {
          const st = (res.data.status || '').toUpperCase();
          if (st === 'UPLOADED' || st === 'COMPLETED' || st === 'READY') {
            setRecordingStatus('READY');
          } else if (st === 'RECORDING' || st === 'PROCESSING' || st === 'IN_PROGRESS') {
            setRecordingStatus('PROCESSING');
          } else if (st === 'FAILED') {
            setRecordingStatus('FAILED');
          } else {
            setRecordingStatus(st);
          }
        }
        if (res.data.captureMode) setCaptureMode(res.data.captureMode);
        if (res.data.mimeType) setMimeType(res.data.mimeType);
        if (res.data.errorReason) setErrorReason(res.data.errorReason);
        if (res.data.storageProvider) setStorageProvider(res.data.storageProvider);
        if (res.data.durationSeconds) setDurationSeconds(res.data.durationSeconds);
      }
    } catch {
      // Non-blocking
    } finally {
      setLoading(false);
    }
  };

  // Fetch signed Cloudinary or backend stream URL
  useEffect(() => {
    let active = true;

    async function fetchRecordingDetails() {
      if (!attemptId) return;
      if (initialVideoUrl) {
        setResolvedUrl(normalizeMediaUrl(initialVideoUrl));
        setRecordingStatus('READY');
        setLoading(false);
        return;
      }

      // Check client-side IndexedDB for durable recorded meeting blob
      let foundLocal = false;
      try {
        const idbBlob = await getSessionRecordingBlob(attemptId);
        if (idbBlob && idbBlob.size > 0 && active) {
          const idbUrl = registerBlobUrl(URL.createObjectURL(idbBlob));
          setResolvedUrl(idbUrl);
          setRecordingStatus('READY');
          setVideoHasError(false);
          foundLocal = true;
        }
      } catch (idbErr) {
        console.warn('[AssessmentRecordingReplayCard] IDB check note:', idbErr);
      }

      // Check local session storage fallback if user just completed the assessment
      const localVideo = typeof window !== 'undefined' ? sessionStorage.getItem(`trainova_attempt_video_${attemptId}`) : null;
      if (localVideo && !foundLocal && active) {
        setResolvedUrl(normalizeMediaUrl(localVideo));
        setRecordingStatus('READY');
        setVideoHasError(false);
        foundLocal = true;
      }

      try {
        if (!foundLocal) setLoading(true);
        const res = await videoApi.getRecording(attemptId);
        if (!active) return;

        if (res.success && res.data) {
          const streamUrl = normalizeMediaUrl(res.data.playbackUrl || res.data.downloadUrl || res.data.publicUrl);
          if (streamUrl) {
            setResolvedUrl(streamUrl);
            setRecordingStatus('READY');
            setVideoHasError(false);
          } else if (res.data.status) {
            const st = (res.data.status || '').toUpperCase();
            if (st === 'UPLOADED' || st === 'COMPLETED' || st === 'READY') {
              setRecordingStatus('READY');
            } else if (st === 'RECORDING' || st === 'PROCESSING' || st === 'IN_PROGRESS') {
              setRecordingStatus('PROCESSING');
            } else if (st === 'FAILED') {
              setRecordingStatus('FAILED');
            } else {
              setRecordingStatus(st);
            }
          }
          if (res.data.captureMode) setCaptureMode(res.data.captureMode);
          if (res.data.mimeType) setMimeType(res.data.mimeType);
          if (res.data.errorReason) setErrorReason(res.data.errorReason);
          if (res.data.storageProvider) setStorageProvider(res.data.storageProvider || 'cloudinary');
          if (res.data.durationSeconds) {
            setDurationSeconds(res.data.durationSeconds);
          }
        } else if (!foundLocal) {
          setRecordingStatus('UNAVAILABLE');
        }
      } catch {
        if (!foundLocal && !initialVideoUrl) {
          setRecordingStatus('UNAVAILABLE');
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    fetchRecordingDetails();
    preloadVoices();

    return () => {
      active = false;
      stopAudioPlayback();
    };
  }, [attemptId, initialVideoUrl, stopAudioPlayback]);

  // Full session continuous recording is progressively streamed directly by HTML5 media elements via HTTP Range requests without buffering full files into uncompressed RAM

  // Auto-scroll active transcript turn into view during playback within the transcript container only
  useEffect(() => {
    if (!transcriptContainerRef.current) return;
    if (activeTurnIndex >= 0 && activeTurnRef.current && activeTurnIndex !== lastScrolledTurnIdxRef.current) {
      lastScrolledTurnIdxRef.current = activeTurnIndex;
      const container = transcriptContainerRef.current;
      const element = activeTurnRef.current;
      const offsetTop = element.offsetTop - container.offsetTop;
      container.scrollTo({
        top: Math.max(0, offsetTop - (container.clientHeight / 2) + (element.clientHeight / 2)),
        behavior: 'smooth',
      });
    }
  }, [activeTurnIndex]);

  // Full cleanup on unmount
  useEffect(() => {
    return () => {
      stopAudioPlayback();
      if (audioContextRef.current) {
        if (audioContextRef.current.state !== 'closed') {
          try { audioContextRef.current.close(); } catch {}
        }
        audioContextRef.current = null;
      }
    };
  }, [stopAudioPlayback]);

  /** Play single turn voice with generation lock protection against overlapping voices */
  const playTurnVoice = (
    turn: {
      id?: string;
      text: string;
      role: 'learner' | 'persona';
      audioUrl?: string;
      audioStartSec?: number;
      audioDurationSec?: number;
      turnSec: number;
    },
    onDone?: () => void
  ) => {
    const thisId = playbackIdRef.current;
    if (!turn.text || !turn.text.trim()) {
      if (onDone) onDone();
      return;
    }

    stopAudioPlayback();
    isAudioPlayingRef.current = true;
    setCurrentSpeaker(turn.role);
    setCurrentSubtitle(turn.text);

    if (isMuted) {
      const words = turn.text.split(' ').length;
      const ms = Math.max(1500, (words / (2.5 * playbackSpeed)) * 1000);
      learnerAudioTimeoutRef.current = setTimeout(() => {
        if (thisId !== playbackIdRef.current) return;
        isAudioPlayingRef.current = false;
        if (onDone) onDone();
      }, ms);
      return;
    }

    // LEARNER TURN: REAL HUMAN VOICE FROM MICROPHONE RECORDING & VIDEO
    if (turn.role === 'learner') {
      const startSec = typeof turn.audioStartSec === 'number' ? turn.audioStartSec : turn.turnSec;
      const durationSec = typeof turn.audioDurationSec === 'number'
        ? turn.audioDurationSec
        : Math.max(3, Math.round(turn.text.split(' ').length / 2.5));

      const playLearnerSpokenFallback = () => {
        if (thisId !== playbackIdRef.current) return;
        // If video element is loaded and working, play video alongside spoken text
        if (videoRef.current && resolvedUrl && !videoHasError) {
          try {
            const video = videoRef.current;
            video.currentTime = Math.max(0, startSec);
            video.muted = true; // muted because speech synthesis will provide the voice
            video.playbackRate = playbackSpeed;
            video.play().catch(() => {});
          } catch {}
        }

        const handle = speakSpeechSynthesis({
          text: turn.text,
          role: 'learner',
          learnerName: (turn as any).speakerName || learnerName || 'Learner',
          rateMultiplier: playbackSpeed,
          onStart: () => {
            if (thisId !== playbackIdRef.current) return;
            setCurrentSpeaker('learner');
            setCurrentSubtitle(turn.text);
          },
          onEnd: () => {
            if (thisId !== playbackIdRef.current) return;
            if (videoRef.current) {
              try { videoRef.current.pause(); } catch {}
            }
            isAudioPlayingRef.current = false;
            if (onDone) onDone();
          },
          onError: () => {
            if (thisId !== playbackIdRef.current) return;
            if (videoRef.current) {
              try { videoRef.current.pause(); } catch {}
            }
            isAudioPlayingRef.current = false;
            if (onDone) onDone();
          },
        });
        activeSpeechCancelRef.current = handle.cancel;
      };

      const tryPlayTurnAudio = (audioSrc: string): boolean => {
        try {
          const audio = new Audio(audioSrc);
          activeAudioElementRef.current = audio;
          audio.playbackRate = playbackSpeed;
          audio.onplay = () => {
            if (thisId !== playbackIdRef.current) return;
            setCurrentSpeaker('learner');
            setCurrentSubtitle(turn.text);
            if (videoRef.current && resolvedUrl && !videoHasError) {
              try {
                videoRef.current.currentTime = Math.max(0, startSec);
                videoRef.current.muted = true;
                videoRef.current.playbackRate = playbackSpeed;
                videoRef.current.play().catch(() => {});
              } catch {}
            }
          };
          audio.onended = () => {
            if (thisId !== playbackIdRef.current) return;
            if (videoRef.current) {
              try { videoRef.current.pause(); } catch {}
            }
            activeAudioElementRef.current = null;
            isAudioPlayingRef.current = false;
            if (onDone) onDone();
          };
          audio.onerror = () => {
            if (thisId !== playbackIdRef.current) return;
            activeAudioElementRef.current = null;
            playLearnerSpokenFallback();
          };
          audio.play().catch(() => {
            if (thisId !== playbackIdRef.current) return;
            playLearnerSpokenFallback();
          });
          return true;
        } catch {
          return false;
        }
      };

      // 1. Try turn.audioUrl if present
      if (turn.audioUrl && turn.audioUrl.trim()) {
        const started = tryPlayTurnAudio(turn.audioUrl);
        if (started) return;
      }

      // 2. Try IndexedDB turn audio if available
      const turnKey = (turn as any).turnNumber ?? (turn as any).originalIndex;
      if (attemptId && turnKey !== undefined) {
        getTurnAudioBlob(attemptId, turnKey).then((blob) => {
          if (thisId !== playbackIdRef.current) return;
          if (blob && blob.size > 0) {
            const blobUrl = registerBlobUrl(URL.createObjectURL(blob));
            if (tryPlayTurnAudio(blobUrl)) return;
          }
          playVideoOrFallback();
        }).catch(() => {
          if (thisId !== playbackIdRef.current) return;
          playVideoOrFallback();
        });
        return;
      }

      playVideoOrFallback();

      function playVideoOrFallback() {
        if (thisId !== playbackIdRef.current) return;
        const media = getSessionMedia();
        if (media && resolvedUrl && !videoHasError) {
          try {
            const medDur = (media.duration && !isNaN(media.duration) && isFinite(media.duration) && media.duration > 0)
              ? media.duration
              : totalDurationSec;

            let validStart = Math.max(0, startSec);
            if (medDur > 0 && validStart >= medDur - 1) {
              validStart = Math.max(0, validStart % Math.max(5, medDur - durationSec));
            }

            media.currentTime = validStart;
            media.muted = isMuted;
            media.volume = isMuted ? 0 : 1;
            media.playbackRate = playbackSpeed;

            if (media === videoRef.current && audioMediaRef.current) {
              try { audioMediaRef.current.pause(); } catch {}
            } else if (media === audioMediaRef.current && videoRef.current) {
              try { videoRef.current.pause(); } catch {}
            }

            let turnFinished = false;
            const finishLearnerTurn = () => {
              if (thisId !== playbackIdRef.current) return;
              if (turnFinished) return;
              turnFinished = true;
              if (learnerAudioTimeoutRef.current) {
                clearTimeout(learnerAudioTimeoutRef.current);
                learnerAudioTimeoutRef.current = null;
              }
              try { media.pause(); } catch {}
              isAudioPlayingRef.current = false;
              if (onDone) onDone();
            };

            learnerAudioTimeoutRef.current = setTimeout(() => {
              finishLearnerTurn();
            }, ((durationSec / playbackSpeed) * 1000) + 150);

            media.play().catch((playErr) => {
              if (thisId !== playbackIdRef.current) return;
              console.warn('[AssessmentRecordingReplayCard] Media play notice, trying spoken fallback:', playErr);
              playLearnerSpokenFallback();
            });
            return;
          } catch (mErr) {
            console.warn('[AssessmentRecordingReplayCard] Media element play exception:', mErr);
          }
        }

        playLearnerSpokenFallback();
      }

      return;
    }

    // AI PERSONA TURN: Authentic AI Persona voice matching live simulation (OpenAI / Sarvam)
    function personaSynth() {
      if (thisId !== playbackIdRef.current) return;
      if (videoRef.current) {
        try { videoRef.current.pause(); } catch {}
      }
      if (audioMediaRef.current) {
        try { audioMediaRef.current.pause(); } catch {}
      }
      const rawTurnPersonaName = (turn as any).personaName || (turn as any).speakerName || personaName || '';
      const cleanPersonaName = rawTurnPersonaName.replace(/\s*\([^)]*\)/g, '').trim() || personaName;
      const effectiveTurnGender = (turn as any).genderPreference || genderPreference || (isFemaleName(cleanPersonaName) ? 'female' : 'male');
      const effectiveTurnVoice = (turn as any).ttsVoice || ttsVoice || (effectiveTurnGender === 'male' ? 'onyx' : 'nova');
      const effectiveTurnCountry = (turn as any).country || country;
      const effectiveTurnTone = (turn as any).tone || tone;

      function browserFallbackSynth() {
        if (thisId !== playbackIdRef.current) return;
        const handle = speakSpeechSynthesis({
          text: turn.text,
          role: 'persona',
          personaName: cleanPersonaName,
          learnerName,
          ttsVoice: effectiveTurnVoice,
          pitch: (turn as any).pitchPreference,
          rate: (turn as any).speakingPace,
          country: effectiveTurnCountry,
          tone: effectiveTurnTone,
          genderPreference: effectiveTurnGender,
          rateMultiplier: playbackSpeed,
          onStart: () => {
            if (thisId !== playbackIdRef.current) return;
            setCurrentSpeaker('persona');
            setCurrentSubtitle(turn.text);
          },
          onEnd: () => {
            if (thisId !== playbackIdRef.current) return;
            isAudioPlayingRef.current = false;
            if (onDone) onDone();
          },
          onError: () => {
            if (thisId !== playbackIdRef.current) return;
            isAudioPlayingRef.current = false;
            if (onDone) onDone();
          },
        });
        activeSpeechCancelRef.current = handle.cancel;
      }

      // Synthesize using authentic AI Persona voice via backend TTS engine
      if (attemptId && turn.text && turn.text.trim()) {
        attemptsApi.synthesizeSpeech(attemptId, {
          text: turn.text,
          ttsVoice: effectiveTurnVoice,
          ttsProvider: (turn as any).ttsProvider,
          country: effectiveTurnCountry,
          tone: effectiveTurnTone,
          speakingPace: (turn as any).speakingPace,
          pitchPreference: (turn as any).pitchPreference,
          genderPreference: effectiveTurnGender,
        }).then((res: any) => {
          if (thisId !== playbackIdRef.current) return;
          const rawBase64 = res?.data?.audioBase64 || res?.audioBase64;
          if (res && res.success && rawBase64) {
            const mime = res.data?.mimeType || res.mimeType || 'audio/mpeg';
            const dataUrl = `data:${mime};base64,${rawBase64}`;
            (turn as any).audioUrl = dataUrl;
            try {
              const audio = new Audio(dataUrl);
              activeAudioElementRef.current = audio;
              audio.playbackRate = playbackSpeed;
              audio.onplay = () => {
                if (thisId !== playbackIdRef.current) return;
                setCurrentSpeaker('persona');
                setCurrentSubtitle(turn.text);
              };
              audio.onended = () => {
                if (thisId !== playbackIdRef.current) return;
                activeAudioElementRef.current = null;
                isAudioPlayingRef.current = false;
                if (onDone) onDone();
              };
              audio.onerror = () => {
                if (thisId !== playbackIdRef.current) return;
                activeAudioElementRef.current = null;
                browserFallbackSynth();
              };
              audio.play().catch(() => {
                if (thisId !== playbackIdRef.current) return;
                browserFallbackSynth();
              });
              return;
            } catch {
              browserFallbackSynth();
              return;
            }
          }
          browserFallbackSynth();
        }).catch(() => {
          if (thisId !== playbackIdRef.current) return;
          browserFallbackSynth();
        });
        return;
      }

      browserFallbackSynth();
    }

    if (turn.audioUrl && turn.audioUrl.trim()) {
      try {
        const audio = new Audio(turn.audioUrl);
        activeAudioElementRef.current = audio;
        audio.playbackRate = playbackSpeed;
        audio.onplay = () => {
          if (thisId !== playbackIdRef.current) return;
          setCurrentSpeaker('persona');
          setCurrentSubtitle(turn.text);
        };
        audio.onended = () => {
          if (thisId !== playbackIdRef.current) return;
          activeAudioElementRef.current = null;
          isAudioPlayingRef.current = false;
          if (onDone) onDone();
        };
        audio.onerror = () => {
          if (thisId !== playbackIdRef.current) return;
          activeAudioElementRef.current = null;
          personaSynth();
        };
        audio.play().catch(() => {
          if (thisId !== playbackIdRef.current) return;
          personaSynth();
        });
        return;
      } catch {}
    }

    personaSynth();
  };

  /** Play continuous turns sequentially for SYNTH engine */
  const playTurnAtIndex = (idx: number) => {
    playTurnAtIndexRef.current = playTurnAtIndex;
    const thisId = playbackIdRef.current;
    if (idx < 0 || idx >= cleanTurns.length) {
      if (isLoopingRef.current && !isSegmentModeRef.current && cleanTurns.length > 0) {
        playTurnAtIndex(0);
        return;
      }
      setIsPlaying(false);
      isPlayingRef.current = false;
      stopAudioPlayback();
      setCurrentTime(totalDurationSec);
      setActiveTurnIndex(-1);
      setCurrentSpeaker(null);
      setCurrentSubtitle(null);
      return;
    }

    setIsPlaying(true);
    isPlayingRef.current = true;
    currentTurnIdxRef.current = idx;
    setActiveTurnIndex(idx);
    activeTurnIndexRef.current = idx;

    const turn = cleanTurns[idx];
    setCurrentTime(turn.turnSec);
    setCurrentSpeaker(turn.role);
    setCurrentSubtitle(turn.text);
    lastPlayedTurnIdRef.current = turn.id;

    playTurnVoice(turn, () => {
      if (thisId !== playbackIdRef.current) return;
      if (isPlayingRef.current && !isSegmentModeRef.current) {
        playTurnAtIndex(idx + 1);
      }
    });
  };

  /**
   * SEGMENT PLAYBACK ON TRANSCRIPT CLICK:
   * 1. Stops current audio immediately.
   * 2. Plays ONLY the selected segment from its exact timestamp.
   * 3. Rapid clicks cancel previous playback immediately; zero overlapping audio.
   * 4. Cleans up audio streams, timers, and listeners.
   */
  const handleTranscriptTurnClick = (idx: number) => {
    if (idx < 0 || idx >= cleanTurns.length) return;

    // Increment generation token: invalidates any prior running async audio/speech immediately
    const thisId = ++playbackIdRef.current;

    // Immediately stop running audio/video/speech
    stopAudioPlayback();

    // Mark as segment playback mode
    isSegmentModeRef.current = true;

    const turn = cleanTurns[idx];
    const nextTurn = cleanTurns[idx + 1];

    // Synchronize UI elements, subtitles, speaker tags and active highlighting immediately
    setActiveTurnIndex(idx);
    activeTurnIndexRef.current = idx;
    currentTurnIdxRef.current = idx;
    setCurrentTime(turn.turnSec);
    setCurrentSpeaker(turn.role);
    setCurrentSubtitle(turn.text);
    setIsPlaying(true);
    isPlayingRef.current = true;
    lastPlayedTurnIdRef.current = turn.id;

    // Determine segment bounds: play ONLY this dialogue turn
    let segDurationSec = turn.audioDurationSec || 4;
    if (nextTurn && nextTurn.turnSec > turn.turnSec) {
      segDurationSec = Math.min(segDurationSec, Math.max(1.5, nextTurn.turnSec - turn.turnSec));
    }
    segDurationSec = Math.max(1.5, segDurationSec);
    const segEnd = turn.turnSec + segDurationSec;
    segmentEndSecRef.current = segEnd;

    claimPlayback('assessment-recording-card', () => {
      if (thisId === playbackIdRef.current) {
        setIsPlaying(false);
        isPlayingRef.current = false;
        isSegmentModeRef.current = false;
        segmentEndSecRef.current = null;
        stopAudioPlayback();
      }
    });

    // Web Audio context unlock
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().catch(() => {});
    }

    // SESSION recording engine: seek to turn.turnSec and play for segDurationSec then pause
    if (useSessionEngine) {
      const el = getSessionMedia();
      if (el) {
        try {
          el.playbackRate = playbackSpeed;
          el.muted = isMuted;
          const maxDur = (el.duration && isFinite(el.duration) && el.duration > 0) ? el.duration : totalDurationSec;
          const targetTime = Math.max(0, Math.min(maxDur, turn.turnSec * (sessionScale || 1)));
          if (isFinite(targetTime)) el.currentTime = targetTime;
        } catch {}

        if (el === videoRef.current && audioMediaRef.current) {
          try { audioMediaRef.current.pause(); } catch {}
        } else if (el === audioMediaRef.current && videoRef.current) {
          try { videoRef.current.pause(); } catch {}
        }

        el.play().then(() => {
          if (thisId !== playbackIdRef.current) {
            try { el.pause(); } catch {}
          }
        }).catch((err) => {
          if (thisId !== playbackIdRef.current) return;
          console.warn('[AssessmentRecordingReplayCard] Session segment play notice:', err);
          // Fall back to turn voice if video failed
          playTurnVoice(turn, () => {
            if (thisId !== playbackIdRef.current) return;
            setIsPlaying(false);
            isPlayingRef.current = false;
            isSegmentModeRef.current = false;
            segmentEndSecRef.current = null;
          });
        });

        // Set segment end timer to pause precisely at the end of this turn
        const durationMs = (segDurationSec / playbackSpeed) * 1000;
        segmentTimeoutRef.current = setTimeout(() => {
          if (thisId !== playbackIdRef.current) return;
          try { el.pause(); } catch {}
          setIsPlaying(false);
          isPlayingRef.current = false;
          isSegmentModeRef.current = false;
          segmentEndSecRef.current = null;
          setCurrentTime(segEnd);
        }, durationMs);
        return;
      }
    }

    // SYNTH per-turn mode: play only this turn and pause cleanly
    playTurnVoice(turn, () => {
      if (thisId !== playbackIdRef.current) return;
      setIsPlaying(false);
      isPlayingRef.current = false;
      isSegmentModeRef.current = false;
      segmentEndSecRef.current = null;
    });
  };

  /** Switch between full-session recording and per-turn replay */
  const switchEngine = (next: 'SESSION' | 'SYNTH') => {
    if (next === playbackEngine) return;
    if (next === 'SESSION' && !sessionEngineAvailable) return;
    ++playbackIdRef.current;
    setIsPlaying(false);
    isPlayingRef.current = false;
    isSegmentModeRef.current = false;
    segmentEndSecRef.current = null;
    pauseSessionRecording();
    stopAudioPlayback();
    currentSpeakingAiTurnIdRef.current = null;
    setPlaybackEngine(next);
    if (next === 'SYNTH') {
      syncTranscriptToTime(currentTime);
    }
  };

  /** Main Transport Play/Pause: handles continuous full-session replay */
  const togglePlay = () => {
    if (isPlaying) {
      ++playbackIdRef.current;
      setIsPlaying(false);
      isPlayingRef.current = false;
      isSegmentModeRef.current = false;
      segmentEndSecRef.current = null;
      releasePlayback('assessment-recording-card');
      if (useSessionEngine) {
        pauseSessionRecording();
      } else {
        stopAudioPlayback();
        pauseSpeech();
      }
      currentSpeakingAiTurnIdRef.current = null;
    } else {
      const thisId = ++playbackIdRef.current;
      stopAudioPlayback();
      isSegmentModeRef.current = false;
      segmentEndSecRef.current = null;

      claimPlayback('assessment-recording-card', () => {
        if (thisId === playbackIdRef.current) {
          setIsPlaying(false);
          isPlayingRef.current = false;
          stopAudioPlayback();
        }
      });
      setIsPlaying(true);
      isPlayingRef.current = true;

      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {});
      }

      if (useSessionEngine) {
        const restartFromZero = currentTime >= totalDurationSec;
        const startPos = restartFromZero ? 0 : currentTime;
        if (restartFromZero) {
          setCurrentTime(0);
          syncTranscriptToTime(0);
        }
        const started = playSessionRecording(startPos);
        if (!started) {
          playTurnAtIndex(activeTurnIndex >= 0 ? activeTurnIndex : 0);
        }
        return;
      }

      if (activeViewMode === 'RAW_VIDEO' && videoRef.current && resolvedUrl && !videoHasError && !isAudioOnly) {
        videoRef.current.play().catch(() => {
          setVideoHasError(true);
          playTurnAtIndex(activeTurnIndex >= 0 ? activeTurnIndex : 0);
        });
      } else {
        const startIdx = (activeTurnIndex >= 0 && activeTurnIndex < cleanTurns.length)
          ? activeTurnIndex
          : 0;
        playTurnAtIndex(startIdx);
      }
    }
  };

  // Timer ticker during play to advance scrubber position smoothly within the active turn's time window (for SYNTH mode)
  useEffect(() => {
    if (isPlaying && !useSessionEngine) {
      playTimerRef.current = setInterval(() => {
        setCurrentTime((prev) => {
          if (prev >= totalDurationSec) {
            return totalDurationSec;
          }
          const curIdx = currentTurnIdxRef.current;
          const nextTurn = cleanTurns[curIdx + 1];
          const maxAllowed = nextTurn ? nextTurn.turnSec - 1 : totalDurationSec;
          if (prev < maxAllowed) {
            return prev + 1;
          }
          return prev;
        });
      }, 1000 / playbackSpeed);
    } else {
      if (playTimerRef.current) clearInterval(playTimerRef.current);
    }

    return () => {
      if (playTimerRef.current) clearInterval(playTimerRef.current);
    };
  }, [isPlaying, useSessionEngine, playbackSpeed, totalDurationSec, cleanTurns]);

  const handleViewModeChange = useCallback((mode: 'MEET_GRID' | 'RAW_VIDEO') => {
    if (mode === activeViewMode) return;
    const media = getSessionMedia();
    const curTime = media && isFinite(media.currentTime) && media.currentTime > 0 ? media.currentTime : currentTimeRef.current;
    const wasPlaying = isPlayingRef.current;
    setActiveViewMode(mode);
    setTimeout(() => {
      const newMedia = getSessionMedia();
      if (newMedia) {
        try {
          if (isFinite(curTime)) newMedia.currentTime = curTime;
          newMedia.playbackRate = playbackSpeed;
          newMedia.muted = isMuted;
          if (wasPlaying) {
            newMedia.play().catch(() => {});
          }
        } catch {}
      }
    }, 60);
  }, [activeViewMode, getSessionMedia, isMuted, playbackSpeed]);

  const handleSeek = (targetSec: number, playImmediately = false, explicitTurnIdx?: number) => {
    const clamped = Math.max(0, Math.min(totalDurationSec, targetSec));
    setCurrentTime(clamped);
    currentSpeakingAiTurnIdRef.current = null;
    isSegmentModeRef.current = false;
    segmentEndSecRef.current = null;

    if (useSessionEngine) {
      const el = getSessionMedia();
      if (el) {
        try { el.currentTime = clamped * (sessionScale || 1); } catch {}
      }
    } else {
      stopAudioPlayback();
      if (audioMediaRef.current) {
        audioMediaRef.current.currentTime = clamped;
      }
      if (videoRef.current) {
        videoRef.current.currentTime = clamped;
      }
    }

    let targetIdx = typeof explicitTurnIdx === 'number' && explicitTurnIdx >= 0 && explicitTurnIdx < cleanTurns.length
      ? explicitTurnIdx
      : cleanTurns.findIndex((t, i) =>
          clamped >= t.turnSec &&
          (i === cleanTurns.length - 1 || clamped < cleanTurns[i + 1].turnSec)
        );
    if (targetIdx === -1) targetIdx = cleanTurns.findIndex(t => t.turnSec >= clamped);
    if (targetIdx === -1) targetIdx = 0;

    setActiveTurnIndex(targetIdx);
    activeTurnIndexRef.current = targetIdx;
    currentTurnIdxRef.current = targetIdx;

    const targetTurn = cleanTurns[targetIdx];
    if (targetTurn) {
      setCurrentSubtitle(targetTurn.text);
      setCurrentSpeaker(targetTurn.role);
    }

    if (playImmediately || isPlaying) {
      setIsPlaying(true);
      isPlayingRef.current = true;
      if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
        audioContextRef.current.resume().catch(() => {});
      }
      if (useSessionEngine) {
        if (!playSessionRecording(clamped)) {
          playTurnAtIndex(targetIdx);
        }
      } else {
        playTurnAtIndex(targetIdx);
      }
    }
  };

  const handleRewind10 = () => {
    handleSeek(currentTime - 10);
  };

  const handleForward10 = () => {
    handleSeek(currentTime + 10);
  };

  const toggleMute = () => {
    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = nextMuted ? 0 : 1;
    }
    if (audioMediaRef.current) {
      audioMediaRef.current.muted = nextMuted;
      audioMediaRef.current.volume = nextMuted ? 0 : 1;
    }
    if (videoRef.current) {
      videoRef.current.muted = nextMuted;
      videoRef.current.volume = nextMuted ? 0 : 1;
    }
    if (nextMuted && !resolvedUrl) {
      stopAudioPlayback();
    }
  };

  const handleSpeedChange = (spd: number) => {
    setPlaybackSpeed(spd);
    if (activeBufferSourceRef.current) {
      try {
        activeBufferSourceRef.current.playbackRate.value = spd;
      } catch {}
    }
    if (audioMediaRef.current) {
      audioMediaRef.current.playbackRate = spd;
    }
    if (videoRef.current) {
      videoRef.current.playbackRate = spd;
    }
  };

  const speeds = [1, 1.25, 1.5, 2];
  const cycleSpeed = () => {
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextSpeed = speeds[(currentIndex + 1) % speeds.length];
    handleSpeedChange(nextSpeed);
  };

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const renderTranscriptPanel = (isSidePanel: boolean, isMobileFullPageBottom: boolean = false) => (
    <div
      style={{
        borderTop: isSidePanel ? 'none' : '1px solid rgba(255, 255, 255, 0.08)',
        background: '#070C18',
        padding: isSidePanel
          ? (isMobileScreen ? '10px 12px' : '16px')
          : (isMobileScreen ? '10px 12px' : '18px 20px'),
        display: 'flex',
        flexDirection: 'column',
        height: (isSidePanel || isMobileFullPageBottom) ? '100%' : undefined,
        flex: isMobileFullPageBottom ? 1 : undefined,
        minHeight: 0,
        overflow: isMobileFullPageBottom ? 'hidden' : undefined,
        boxSizing: 'border-box',
      }}
    >
      {/* Top Header of Transcript */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: isMobileScreen ? '8px' : '12px', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: isMobileScreen ? '12.5px' : '13.5px', fontWeight: 700, color: '#F8FAFC', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>💬</span> Synchronized Transcript
          </span>
          <span style={{ fontSize: '10.5px', fontWeight: 600, color: '#38BDF8', background: 'rgba(56, 189, 248, 0.14)', padding: '2px 7px', borderRadius: '4px', border: '1px solid rgba(56, 189, 248, 0.25)' }}>
            {displayedTurns.length}{transcriptSearch ? ` of ${cleanTurns.length}` : ''} Turns
          </span>
          {useSessionEngine && (
            <span
              style={{ fontSize: '10.5px', fontWeight: 600, color: '#86EFAC', background: 'rgba(34, 197, 94, 0.14)', padding: '2px 7px', borderRadius: '4px', border: '1px solid rgba(34, 197, 94, 0.25)' }}
              title="Following the continuous audio/video captured during the simulation"
            >
              🎬 Live mix
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: (isSidePanel || isMobileScreen) ? '100%' : 'auto' }}>
          <input
            type="text"
            placeholder="Search transcript..."
            value={transcriptSearch}
            onChange={(e) => setTranscriptSearch(e.target.value)}
            style={{
              background: '#162032',
              border: '1px solid #2B3A55',
              borderRadius: '6px',
              padding: isMobileScreen ? '4px 8px' : '5px 10px',
              fontSize: '12px',
              color: '#FFFFFF',
              outline: 'none',
              width: (isSidePanel || isMobileScreen) ? '100%' : '180px',
              boxSizing: 'border-box',
            }}
          />
          {transcriptSearch && (
            <button
              type="button"
              onClick={() => setTranscriptSearch('')}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#94A3B8',
                cursor: 'pointer',
                fontSize: '12px',
                padding: '2px 4px',
              }}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Helpful Click Instruction Tip */}
      <div
        style={{
          fontSize: isMobileScreen ? '10.5px' : '11px',
          color: '#94A3B8',
          background: 'rgba(13, 79, 189, 0.1)',
          border: '1px solid rgba(13, 79, 189, 0.2)',
          borderRadius: '6px',
          padding: isMobileScreen ? '4px 8px' : '5px 10px',
          marginBottom: isMobileScreen ? '6px' : '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}
      >
        <span>💡</span>
        <span>Tap any turn below to play that exact dialogue segment.</span>
      </div>

      {/* Transcript Turns List */}
      <div
        ref={transcriptContainerRef}
        style={{
          maxHeight: (isSidePanel || isMobileFullPageBottom) ? undefined : '280px',
          flex: (isSidePanel || isMobileFullPageBottom) ? 1 : undefined,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: isMobileScreen ? '6px' : '8px',
          paddingRight: '6px',
          minHeight: 0,
        }}
      >
        {displayedTurns.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', color: '#64748B', fontSize: '13px' }}>
            No conversation turns match "{transcriptSearch}"
          </div>
        ) : (
          displayedTurns.map((turn, i) => {
            const isTurnActive = activeTurnIndex >= 0 && cleanTurns[activeTurnIndex]?.id === turn.id;
            const originalTurnIdx = (turn as any).originalIndex !== undefined ? (turn as any).originalIndex : cleanTurns.findIndex(ct => ct.id === turn.id);
            const targetIdx = originalTurnIdx >= 0 ? originalTurnIdx : i;

            return (
              <div
                key={turn.id || i}
                ref={isTurnActive ? activeTurnRef : undefined}
                onClick={() => handleTranscriptTurnClick(targetIdx)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: isMobileScreen ? '8px' : '12px',
                  padding: isMobileScreen ? '8px 10px' : '11px 14px',
                  borderRadius: '10px',
                  background: isTurnActive
                    ? turn.isLearner
                      ? 'rgba(47, 123, 255, 0.16)'
                      : 'rgba(245, 158, 11, 0.16)'
                    : 'rgba(22, 32, 50, 0.45)',
                  borderLeft: isTurnActive
                    ? turn.isLearner
                      ? '4px solid #2F7BFF'
                      : '4px solid #F59E0B'
                    : '4px solid transparent',
                  borderTop: isTurnActive ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(255, 255, 255, 0.03)',
                  borderRight: isTurnActive ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(255, 255, 255, 0.03)',
                  borderBottom: isTurnActive ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(255, 255, 255, 0.03)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  position: 'relative',
                }}
                onMouseEnter={(e) => {
                  if (!isTurnActive) {
                    e.currentTarget.style.background = 'rgba(30, 43, 68, 0.7)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isTurnActive) {
                    e.currentTarget.style.background = 'rgba(22, 32, 50, 0.45)';
                  }
                }}
              >
                {/* Time Badge */}
                <span
                  style={{
                    fontSize: isMobileScreen ? '10px' : '11px',
                    fontWeight: 700,
                    fontFamily: 'monospace',
                    color: isTurnActive ? (turn.isLearner ? '#60A5FA' : '#FBBF24') : '#94A3B8',
                    background: 'rgba(0, 0, 0, 0.5)',
                    padding: isMobileScreen ? '2px 5px' : '3px 7px',
                    borderRadius: '5px',
                    flexShrink: 0,
                    marginTop: '2px',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                  }}
                >
                  {formatTime(turn.turnSec)}
                </span>

                {/* Speaker Avatar Chip */}
                <div
                  style={{
                    width: isMobileScreen ? '22px' : '26px',
                    height: isMobileScreen ? '22px' : '26px',
                    borderRadius: '50%',
                    background: turn.isLearner ? '#0D4FBD' : '#D97706',
                    color: '#FFFFFF',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: isMobileScreen ? '10px' : '11px',
                    fontWeight: 800,
                    flexShrink: 0,
                    marginTop: '1px',
                    boxShadow: isTurnActive ? (turn.isLearner ? '0 0 10px rgba(13, 79, 189, 0.6)' : '0 0 10px rgba(217, 119, 6, 0.6)') : 'none',
                  }}
                >
                  {turn.isLearner ? 'L' : 'AI'}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', marginBottom: '3px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span
                        style={{
                          fontSize: isMobileScreen ? '12px' : '12.5px',
                          fontWeight: 700,
                          color: turn.isLearner ? '#93C5FD' : '#FDE68A',
                        }}
                      >
                        {turn.speakerName}
                      </span>
                      {isTurnActive ? (
                        <span
                          style={{
                            fontSize: '9px',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            background: turn.isLearner ? '#2F7BFF' : '#F59E0B',
                            color: '#FFFFFF',
                            padding: '1px 6px',
                            borderRadius: '4px',
                            letterSpacing: '0.04em',
                          }}
                        >
                          {turn.isLearner ? '🎙️ Playing' : '🤖 Playing'}
                        </span>
                      ) : (
                        <span
                          style={{
                            fontSize: '9px',
                            fontWeight: 600,
                            color: turn.isLearner ? '#60A5FA' : '#FBBF24',
                            background: turn.isLearner ? 'rgba(37, 99, 235, 0.12)' : 'rgba(217, 119, 6, 0.12)',
                            padding: '1px 5px',
                            borderRadius: '3px',
                          }}
                        >
                          {turn.isLearner ? 'Learner' : 'AI Persona'}
                        </span>
                      )}
                    </div>

                    {/* Small Play Hint */}
                    <span
                      style={{
                        fontSize: isMobileScreen ? '9.5px' : '10.5px',
                        color: isTurnActive ? '#38BDF8' : '#64748B',
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      {isTurnActive ? '▶ Active' : '▶ Replay'}
                    </span>
                  </div>

                  <p
                    style={{
                      margin: 0,
                      fontSize: isMobileScreen ? '12.5px' : '13.5px',
                      color: isTurnActive ? '#FFFFFF' : '#E2E8F0',
                      lineHeight: 1.5,
                      fontWeight: isTurnActive ? 500 : 400,
                      wordBreak: 'break-word',
                    }}
                  >
                    {turn.text}
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <div
      style={isFullPage ? {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: isMobileScreen ? '100%' : '100vw',
        maxWidth: '100vw',
        height: isMobileScreen ? '100dvh' : '100vh',
        minHeight: '100vh',
        paddingTop: isMobileScreen ? 'env(safe-area-inset-top, 0px)' : 0,
        paddingBottom: isMobileScreen ? 'env(safe-area-inset-bottom, 0px)' : 0,
        paddingLeft: isMobileScreen ? 'env(safe-area-inset-left, 0px)' : 0,
        paddingRight: isMobileScreen ? 'env(safe-area-inset-right, 0px)' : 0,
        boxSizing: 'border-box',
        zIndex: 99999,
        background: '#070B14',
        borderRadius: 0,
        border: 'none',
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
        color: '#FFFFFF',
        overflow: 'hidden',
        fontFamily: 'var(--body, Figtree, system-ui, sans-serif)',
      } : {
        background: '#0B1120',
        borderRadius: '14px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        boxShadow: '0 8px 32px rgba(7, 29, 69, 0.35)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        gap: '0px',
        color: '#FFFFFF',
        fontFamily: 'var(--body, Figtree, system-ui, sans-serif)',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
      }}
    >
      {/* Header Bar */}
      {isFullPage && isMobileScreen ? (
        <div
          style={{
            padding: '8px 12px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            background: 'linear-gradient(180deg, #131E33 0%, #0B1120 100%)',
            flexShrink: 0,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
          }}
        >
          {/* Row 1: Indicator, Title, Time, View Switcher & Exit Button */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              width: '100%',
            }}
          >
            {/* Left: Red recording dot & Title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, flex: '1 1 auto' }}>
              <span
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: '#EF4444',
                  animation: isPlaying ? 'pulse 1.2s infinite' : 'none',
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: '12.5px',
                  fontWeight: 700,
                  color: '#FFFFFF',
                  fontFamily: 'var(--display, Lexend, sans-serif)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: isLandscape ? '240px' : '110px',
                }}
                title={simulationTitle || 'Assessment Recording'}
              >
                {simulationTitle ? simulationTitle.split('—')[0].trim() : 'Assessment'}
              </span>
              <span
                style={{
                  fontSize: '10.5px',
                  fontWeight: 600,
                  color: '#94A3B8',
                  fontFamily: 'monospace',
                  flexShrink: 0,
                }}
              >
                {formatTime(currentTime)} / {formatTime(totalDurationSec)}
              </span>
            </div>

            {/* Middle: Split / Video / Script Mode Switcher */}
            <div
              style={{
                display: 'flex',
                background: '#162032',
                padding: '2px',
                borderRadius: '6px',
                border: '1px solid #2B3A55',
                gap: '2px',
                flexShrink: 0,
              }}
            >
              <button
                type="button"
                onClick={() => setMobileFsView('split')}
                style={{
                  border: 'none',
                  padding: '3px 8px',
                  borderRadius: '4px',
                  fontSize: '10.5px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: mobileFsView === 'split' ? '#0D4FBD' : 'transparent',
                  color: mobileFsView === 'split' ? '#FFFFFF' : '#94A3B8',
                  transition: 'all 0.15s ease',
                }}
                title="Split View (Video + Transcript)"
              >
                Split
              </button>
              <button
                type="button"
                onClick={() => setMobileFsView('video')}
                style={{
                  border: 'none',
                  padding: '3px 8px',
                  borderRadius: '4px',
                  fontSize: '10.5px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: mobileFsView === 'video' ? '#0D4FBD' : 'transparent',
                  color: mobileFsView === 'video' ? '#FFFFFF' : '#94A3B8',
                  transition: 'all 0.15s ease',
                }}
                title="Video Only"
              >
                Video
              </button>
              <button
                type="button"
                onClick={() => setMobileFsView('transcript')}
                style={{
                  border: 'none',
                  padding: '3px 8px',
                  borderRadius: '4px',
                  fontSize: '10.5px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  background: mobileFsView === 'transcript' ? '#0D4FBD' : 'transparent',
                  color: mobileFsView === 'transcript' ? '#FFFFFF' : '#94A3B8',
                  transition: 'all 0.15s ease',
                }}
                title="Transcript Only"
              >
                Script
              </button>
            </div>

            {/* Right: Exit Full Screen Button */}
            <button
              type="button"
              onClick={() => setIsFullPage(false)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                background: '#0D4FBD',
                color: '#FFFFFF',
                border: '1px solid rgba(56, 189, 248, 0.35)',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '11px',
                fontWeight: 600,
                cursor: 'pointer',
                flexShrink: 0,
              }}
              title="Exit Full Screen"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
              </svg>
              <span>Exit</span>
            </button>
          </div>

          {/* Row 2: Non-wrapping horizontal scrolling badges bar */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              overflowX: 'auto',
              whiteSpace: 'nowrap',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              width: '100%',
              paddingBottom: '2px',
            }}
          >
            {resolvedUrl && !isAudioOnly && (
              <div style={{ display: 'inline-flex', background: '#162032', padding: '2px', borderRadius: '5px', border: '1px solid #2B3A55', gap: '2px', flexShrink: 0 }}>
                <button
                  type="button"
                  onClick={() => handleViewModeChange('MEET_GRID')}
                  style={{
                    border: 'none',
                    padding: '2px 7px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: activeViewMode === 'MEET_GRID' ? '#0D4FBD' : 'transparent',
                    color: activeViewMode === 'MEET_GRID' ? '#FFFFFF' : '#94A3B8',
                  }}
                >
                  👥 Meet Grid
                </button>
                <button
                  type="button"
                  onClick={() => handleViewModeChange('RAW_VIDEO')}
                  style={{
                    border: 'none',
                    padding: '2px 7px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: activeViewMode === 'RAW_VIDEO' ? '#0D4FBD' : 'transparent',
                    color: activeViewMode === 'RAW_VIDEO' ? '#FFFFFF' : '#94A3B8',
                  }}
                >
                  📹 Video Stream
                </button>
              </div>
            )}

            {resolvedUrl && (
              <div
                style={{ display: 'inline-flex', background: '#162032', padding: '2px', borderRadius: '5px', border: '1px solid #2B3A55', gap: '2px', flexShrink: 0 }}
                title="Play continuous recording mix, or replay individual turn voices"
              >
                <button
                  type="button"
                  onClick={() => switchEngine('SESSION')}
                  disabled={!sessionEngineAvailable}
                  style={{
                    border: 'none',
                    padding: '2px 7px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: 600,
                    cursor: sessionEngineAvailable ? 'pointer' : 'not-allowed',
                    opacity: sessionEngineAvailable ? 1 : 0.5,
                    background: playbackEngine === 'SESSION' ? '#0F8A5F' : 'transparent',
                    color: playbackEngine === 'SESSION' ? '#FFFFFF' : '#94A3B8',
                  }}
                >
                  🎬 Full Mix
                </button>
                <button
                  type="button"
                  onClick={() => switchEngine('SYNTH')}
                  style={{
                    border: 'none',
                    padding: '2px 7px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: playbackEngine === 'SYNTH' ? '#0D4FBD' : 'transparent',
                    color: playbackEngine === 'SYNTH' ? '#FFFFFF' : '#94A3B8',
                  }}
                >
                  🗣️ Per-turn
                </button>
              </div>
            )}

            <span
              style={{
                fontSize: '10px',
                fontWeight: 600,
                padding: '2px 6px',
                borderRadius: '999px',
                background: 'rgba(56, 189, 248, 0.12)',
                color: '#38BDF8',
                border: '1px solid rgba(56, 189, 248, 0.25)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                flexShrink: 0,
              }}
            >
              ☁️ {storageProvider === 'cloudinary' ? 'Cloudinary' : 'Secure Cloud'}
            </span>

            <span
              style={{
                fontSize: '10px',
                fontWeight: 600,
                padding: '2px 6px',
                borderRadius: '4px',
                background: 'rgba(255, 255, 255, 0.08)',
                color: '#CBD5E1',
                flexShrink: 0,
              }}
            >
              {learnerName} & {personaName}
            </span>
          </div>
        </div>
      ) : (
        /* Regular Desktop or In-card Header Bar */
        <div
          style={{
            padding: '14px clamp(12px, 3vw, 20px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(180deg, #131E33 0%, #0B1120 100%)',
            flexWrap: 'wrap',
            gap: '12px',
            flexShrink: 0,
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, flex: '1 1 240px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '9px',
                background: 'rgba(239, 68, 68, 0.16)',
                color: '#EF4444',
                display: 'grid',
                placeItems: 'center',
                border: '1px solid rgba(239, 68, 68, 0.35)',
              }}
            >
              <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#EF4444', animation: isPlaying ? 'pulse 1.2s infinite' : 'none' }} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#FFFFFF', letterSpacing: '-0.01em', fontFamily: 'var(--display, Lexend, sans-serif)' }}>
                  Assessment Session Recording
                </h3>
                <span style={{ fontSize: '10.5px', fontWeight: 700, background: 'rgba(47, 123, 255, 0.18)', color: '#38BDF8', border: '1px solid rgba(56, 189, 248, 0.3)', padding: '2px 7px', borderRadius: '4px' }}>
                  GOOGLE MEET REC
                </span>
                {isFullPage && (
                  <span style={{ fontSize: '10.5px', fontWeight: 600, background: 'rgba(56, 189, 248, 0.2)', color: '#38BDF8', padding: '2px 7px', borderRadius: '4px' }}>
                    FULL SCREEN THEATER · ESC TO EXIT
                  </span>
                )}
              </div>
              <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#94A3B8', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                Synchronized simulation replay · {learnerName} & {personaName} {simulationTitle ? `· ${simulationTitle}` : ''} {recordedAt ? `(${new Date(recordedAt).toLocaleDateString()})` : ''}
              </p>
            </div>
          </div>

          {/* Top Badges & View Switcher */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {resolvedUrl && !isAudioOnly && (
              <div style={{ display: 'flex', background: '#162032', padding: '2px', borderRadius: '6px', border: '1px solid #2B3A55', gap: '2px' }}>
                <button
                  type="button"
                  onClick={() => handleViewModeChange('MEET_GRID')}
                  style={{
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: activeViewMode === 'MEET_GRID' ? '#0D4FBD' : 'transparent',
                    color: activeViewMode === 'MEET_GRID' ? '#FFFFFF' : '#94A3B8',
                    transition: 'all 0.15s ease',
                  }}
                >
                  👥 Meet Grid
                </button>
                <button
                  type="button"
                  onClick={() => handleViewModeChange('RAW_VIDEO')}
                  style={{
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: activeViewMode === 'RAW_VIDEO' ? '#0D4FBD' : 'transparent',
                    color: activeViewMode === 'RAW_VIDEO' ? '#FFFFFF' : '#94A3B8',
                    transition: 'all 0.15s ease',
                  }}
                >
                  📹 Video Stream
                </button>
              </div>
            )}

            {/* Playback engine switcher */}
            {resolvedUrl && (
              <div
                style={{ display: 'flex', background: '#162032', padding: '2px', borderRadius: '6px', border: '1px solid #2B3A55', gap: '2px' }}
                title="Play continuous recording mix, or replay individual turn voices"
              >
                <button
                  type="button"
                  onClick={() => switchEngine('SESSION')}
                  disabled={!sessionEngineAvailable}
                  style={{
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 600,
                    cursor: sessionEngineAvailable ? 'pointer' : 'not-allowed',
                    opacity: sessionEngineAvailable ? 1 : 0.5,
                    background: playbackEngine === 'SESSION' ? '#0F8A5F' : 'transparent',
                    color: playbackEngine === 'SESSION' ? '#FFFFFF' : '#94A3B8',
                    transition: 'all 0.15s ease',
                  }}
                >
                  🎬 Full Session Recording
                </button>
                <button
                  type="button"
                  onClick={() => switchEngine('SYNTH')}
                  style={{
                    border: 'none',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    background: playbackEngine === 'SYNTH' ? '#0D4FBD' : 'transparent',
                    color: playbackEngine === 'SYNTH' ? '#FFFFFF' : '#94A3B8',
                    transition: 'all 0.15s ease',
                  }}
                >
                  🗣️ Per-turn Voices
                </button>
              </div>
            )}

            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                padding: '3px 9px',
                borderRadius: '999px',
                background: 'rgba(56, 189, 248, 0.12)',
                color: '#38BDF8',
                border: '1px solid rgba(56, 189, 248, 0.25)',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              <span>☁️</span> {storageProvider === 'cloudinary' ? 'Cloudinary Auto-Archived' : 'Secure Cloud Archived'}
            </span>
            <span style={{ fontSize: '11.5px', fontWeight: 600, color: '#CBD5E1', fontFamily: 'monospace' }}>
              ⏱️ {formatTime(totalDurationSec)}
            </span>

            {/* Full page toggle button */}
            <button
              type="button"
              onClick={() => setIsFullPage(!isFullPage)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: isFullPage ? '#0D4FBD' : 'rgba(13, 79, 189, 0.15)',
                color: isFullPage ? '#FFFFFF' : '#38BDF8',
                border: '1px solid rgba(56, 189, 248, 0.35)',
                borderRadius: '6px',
                padding: '5px 11px',
                fontSize: '11.5px',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
              title={isFullPage ? 'Exit Full Screen (Esc)' : 'Open Full Screen Theater'}
            >
              {isFullPage ? (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                </svg>
              ) : (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                </svg>
              )}
              <span>{isFullPage ? 'Exit full screen' : 'Full screen theater'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Main Body */}
      <div
        style={isFullPage ? {
          flex: 1,
          display: 'flex',
          overflow: 'hidden',
          flexDirection: isMobileScreen && !isLandscape ? 'column' : 'row',
          minHeight: 0,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        } : {
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
        }}
      >
        {/* Left Side: Stage Viewport + Subtitles + Scrubber Controls */}
        <div
          style={{
            flex: isFullPage && isMobileScreen && isLandscape ? '1 1 58%' : 1,
            maxWidth: isFullPage && isMobileScreen && isLandscape ? '60%' : '100%',
            minWidth: 0,
            display: isFullPage && isMobileScreen && !isLandscape && mobileFsView === 'transcript' ? 'none' : 'flex',
            flexDirection: 'column',
            overflowY: isFullPage && (!isMobileScreen || isLandscape) ? 'auto' : 'hidden',
            background: '#070C18',
          }}
        >
          {/* Main Audiovisual Viewport */}
          <div
            style={{
              position: 'relative',
              background: '#070C18',
              minHeight: isMobileScreen
                ? (mobileFsView === 'video' ? (isLandscape ? '180px' : '240px') : 'auto')
                : (isFullPage ? '400px' : '280px'),
              maxHeight: isMobileScreen && isFullPage && mobileFsView === 'split' && !isLandscape ? '34vh' : undefined,
              flex: isFullPage && (mobileFsView === 'video' || !isMobileScreen) ? 1 : undefined,
              flexShrink: isMobileScreen && mobileFsView === 'split' ? 0 : undefined,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {recordingStatus === 'PROCESSING' && (
              <div style={{ padding: '16px 20px', background: 'rgba(245, 158, 11, 0.12)', borderBottom: '1px solid rgba(245, 158, 11, 0.25)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(245, 158, 11, 0.2)', color: '#F59E0B', display: 'grid', placeItems: 'center', fontSize: '15px' }}>
                    ⏳
                  </div>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#FDE68A' }}>
                      Assessment Recording Processing & Finalizing
                    </div>
                    <div style={{ fontSize: '11.5px', color: '#CBD5E1', marginTop: '2px' }}>
                      The session media is being archived and prepared for playback. You can review the evaluation report below, or click Refresh to check readiness.
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={checkStatus}
                  style={{ background: '#F59E0B', color: '#0F172A', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  🔄 Refresh Recording
                </button>
              </div>
            )}

            {recordingStatus === 'FAILED' && (
              <div style={{ padding: '16px 20px', background: 'rgba(239, 68, 68, 0.12)', borderBottom: '1px solid rgba(239, 68, 68, 0.25)', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '20px' }}>⚠️</span>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#FCA5A5' }}>
                    Recording Capture Incomplete
                  </div>
                  <div style={{ fontSize: '11.5px', color: '#CBD5E1', marginTop: '2px' }}>
                    {errorReason || 'The recording media stream could not be finalized. Your transcript and evaluation scores are preserved.'}
                  </div>
                </div>
              </div>
            )}

            {loading ? (
              <div style={{ color: '#94A3B8', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '40px' }}>
                <div className="dashboard-spinner" style={{ width: '20px', height: '20px' }} />
                Syncing Cloudinary Assessment Recording...
              </div>
            ) : activeViewMode === 'RAW_VIDEO' && resolvedUrl && !videoHasError && !isAudioOnly ? (
              <div
                style={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '16/9',
                  maxHeight: isMobileScreen
                    ? (mobileFsView === 'video' ? '70vh' : (isLandscape ? '55vh' : '32vh'))
                    : (isFullPage ? '600px' : '420px'),
                  background: '#000000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                <video
                  ref={videoRef}
                  src={resolvedUrl}
                  playsInline
                  controls={false}
                  onError={(err) => {
                    console.warn('[AssessmentRecordingReplayCard] Raw video playback error, checking fallbacks:', err);
                    if (typeof window !== 'undefined' && attemptId) {
                      const localFallback = sessionStorage.getItem(`trainova_attempt_video_${attemptId}`);
                      if (localFallback && localFallback !== resolvedUrl) {
                        console.log('[AssessmentRecordingReplayCard] Recovering raw video with local session video blob.');
                        setResolvedUrl(localFallback);
                        setVideoHasError(false);
                        return;
                      }
                      getSessionRecordingBlob(attemptId).then((blob) => {
                        if (blob && blob.size > 0) {
                          const idbUrl = registerBlobUrl(URL.createObjectURL(blob));
                          console.log('[AssessmentRecordingReplayCard] Recovering raw video with IndexedDB blob.');
                          setResolvedUrl(idbUrl);
                          setVideoHasError(false);
                          setRecordingStatus('READY');
                          return;
                        }
                        setVideoHasError(true);
                      }).catch(() => setVideoHasError(true));
                      return;
                    }
                    setVideoHasError(true);
                  }}
                  onLoadedMetadata={(e) => {
                    registerSessionMetadata(e.currentTarget);
                  }}
                  onTimeUpdate={() => {
                    if (useSessionEngine) {
                      handleSessionTimeUpdate();
                      return;
                    }
                    if (videoRef.current) {
                      const cur = Math.round(videoRef.current.currentTime);
                      setCurrentTime(cur);
                      syncTranscriptToTime(cur);
                    }
                  }}
                  onEnded={handleMediaEnded}
                  onClick={togglePlay}
                  style={{ width: '100%', height: '100%', objectFit: 'contain', cursor: 'pointer' }}
                />
                {!isPlaying && (
                  <div
                    onClick={togglePlay}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(0,0,0,0.3)',
                      cursor: 'pointer',
                    }}
                  >
                    <div
                      style={{
                        width: isMobileScreen ? '48px' : '60px',
                        height: isMobileScreen ? '48px' : '60px',
                        borderRadius: '50%',
                        background: '#0D4FBD',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 20px rgba(13, 79, 189, 0.6)',
                        cursor: 'pointer',
                        transform: 'scale(1)',
                        transition: 'transform 0.15s ease',
                      }}
                    >
                      <svg width={isMobileScreen ? '20' : '24'} height={isMobileScreen ? '20' : '24'} viewBox="0 0 24 24" fill="#FFFFFF" style={{ marginLeft: '3px' }}>
                        <polygon points="6 3 20 12 6 21 6 3" />
                      </svg>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* Google Meet Dual Stream Responsive Grid Stage */
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobileScreen ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: isMobileScreen ? '8px' : '16px',
                  padding: isMobileScreen ? '8px 10px' : (isFullPage ? '24px' : 'clamp(12px, 2.5vw, 20px)'),
                  background: 'linear-gradient(180deg, #070B14 0%, #0F172A 100%)',
                  alignItems: 'stretch',
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  boxSizing: 'border-box',
                }}
              >
                {/* Tile 1: AI Persona (Customer) */}
                <div
                  className={`assessment-replay-tile lv-tile lv-ai ${currentSpeaker === 'persona' && isPlaying ? 'lv-speaking' : ''}`}
                  style={{
                    position: 'relative',
                    aspectRatio: '16/9',
                    minHeight: isMobileScreen ? '96px' : (isFullPage ? '320px' : '160px'),
                    maxHeight: isMobileScreen && mobileFsView === 'split' ? '170px' : undefined,
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                    borderRadius: isMobileScreen ? '10px' : '16px',
                    overflow: 'hidden',
                    background: '#0F2350',
                    border: currentSpeaker === 'persona' && isPlaying
                      ? '2.5px solid #2F7BFF'
                      : '2px solid rgba(255, 255, 255, 0.08)',
                    boxShadow: currentSpeaker === 'persona' && isPlaying
                      ? '0 0 0 4px rgba(47, 123, 255, 0.22), 0 8px 24px rgba(47, 123, 255, 0.3)'
                      : '0 4px 16px rgba(0,0,0,0.3)',
                    transition: 'all 0.25s ease',
                  }}
                >
                  {/* Persona Avatar / Video taking full tile area matching live simulation */}
                  <div style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'hidden' }}>
                    <SpeechControlledAvatar
                      avatarUrl={personaAvatarUrl}
                      avatarType={personaAvatarType || avatarType}
                      isSpeaking={currentSpeaker === 'persona' && isPlaying}
                      personaName={personaName}
                      genderPreference={genderPreference}
                      className="lv-av-img"
                      renderFallback={() => (
                        <div
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
                          dangerouslySetInnerHTML={{ __html: getPersonaAvatarSVG(personaName) }}
                        />
                      )}
                    />
                  </div>

                  {/* Top Speaker Tag */}
                  <div
                    style={{
                      position: 'absolute',
                      top: isMobileScreen ? '6px' : '12px',
                      left: isMobileScreen ? '6px' : '12px',
                      fontSize: isMobileScreen ? '9px' : '11px',
                      fontWeight: 700,
                      color: currentSpeaker === 'persona' && isPlaying ? '#CFE0FF' : '#F59E0B',
                      background: currentSpeaker === 'persona' && isPlaying ? 'rgba(47, 123, 255, 0.45)' : 'rgba(5, 15, 38, 0.8)',
                      backdropFilter: 'blur(8px)',
                      padding: isMobileScreen ? '2px 6px' : '4px 10px',
                      borderRadius: isMobileScreen ? '4px' : '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: isMobileScreen ? '4px' : '6px',
                      border: currentSpeaker === 'persona' && isPlaying ? '1px solid rgba(47, 123, 255, 0.5)' : '1px solid rgba(255, 255, 255, 0.08)',
                      zIndex: 10,
                      letterSpacing: '0.04em',
                    }}
                  >
                    <span
                      style={{
                        width: isMobileScreen ? '5px' : '7px',
                        height: isMobileScreen ? '5px' : '7px',
                        borderRadius: '50%',
                        background: currentSpeaker === 'persona' && isPlaying ? '#2F7BFF' : '#F59E0B',
                        animation: currentSpeaker === 'persona' && isPlaying ? 'pulse 1s infinite' : 'none',
                      }}
                    />
                    AI{isMobileScreen ? '' : ' PERSONA'} · {personaName}
                  </div>

                  {/* Live Audio Equalizer on Tile */}
                  <div
                    style={{
                      position: 'absolute',
                      top: isMobileScreen ? '6px' : '12px',
                      right: isMobileScreen ? '6px' : '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: isMobileScreen ? '1.5px' : '2.5px',
                      background: 'rgba(5, 15, 38, 0.8)',
                      backdropFilter: 'blur(8px)',
                      padding: isMobileScreen ? '2px 5px' : '4px 8px',
                      borderRadius: isMobileScreen ? '4px' : '6px',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      zIndex: 10,
                    }}
                  >
                    {[10, 18, 26, 14, 20].map((h, i) => (
                      <div
                        key={i}
                        style={{
                          width: isMobileScreen ? '2px' : '2.5px',
                          height: currentSpeaker === 'persona' && isPlaying
                            ? `${Math.max(3, Math.round((isMobileScreen ? h * 0.7 : h) * (Math.sin(currentTime * 4 + i) + 1.2)))}px`
                            : '3px',
                          background: currentSpeaker === 'persona' && isPlaying ? '#2F7BFF' : '#64748B',
                          borderRadius: '1px',
                          transition: 'height 0.1s ease',
                        }}
                      />
                    ))}
                  </div>

                  {/* Bottom-Left Persona Tag Overlay matching Live Simulation */}
                  <div
                    className="lv-tag"
                    style={{
                      position: 'absolute',
                      left: isMobileScreen ? '6px' : '12px',
                      bottom: isMobileScreen ? '6px' : '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: isMobileScreen ? '4px' : '8px',
                      background: 'rgba(5, 15, 38, 0.85)',
                      backdropFilter: 'blur(8px)',
                      padding: isMobileScreen ? '3px 6px' : '6px 12px 6px 8px',
                      borderRadius: isMobileScreen ? '6px' : '10px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      zIndex: 10,
                      maxWidth: 'calc(100% - 12px)',
                    }}
                  >
                    <span
                      style={{
                        fontSize: isMobileScreen ? '9px' : '10px',
                        fontWeight: 800,
                        letterSpacing: '0.06em',
                        background: '#2F7BFF',
                        color: '#FFFFFF',
                        padding: '1px 5px',
                        borderRadius: '3px',
                      }}
                    >
                      AI
                    </span>
                    <b style={{ whiteSpace: 'nowrap', color: '#FFFFFF', fontSize: isMobileScreen ? '11px' : '13px', fontWeight: 600 }}>
                      {personaName}
                    </b>
                    {personaRole && !isMobileScreen && (
                      <span
                        title={personaRole}
                        style={{
                          fontSize: '12px',
                          color: '#9AABCB',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '120px',
                        }}
                      >
                        {personaRole}
                      </span>
                    )}
                    {personaMood && personaMood !== 'Neutral' && (
                      <span
                        style={{
                          fontSize: isMobileScreen ? '9px' : '10px',
                          padding: '1px 5px',
                          borderRadius: '3px',
                          background: 'rgba(255, 255, 255, 0.12)',
                          color: '#FDE047',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {personaMood}
                      </span>
                    )}
                  </div>

                  {/* Bottom-Right Audio Waveform Bars matching Live Simulation */}
                  {currentSpeaker === 'persona' && isPlaying && (
                    <div
                      className="lv-wave"
                      style={{
                        position: 'absolute',
                        right: isMobileScreen ? '6px' : '12px',
                        bottom: isMobileScreen ? '6px' : '12px',
                        display: 'flex',
                        gap: '2px',
                        alignItems: 'flex-end',
                        background: 'rgba(5, 15, 38, 0.85)',
                        backdropFilter: 'blur(8px)',
                        padding: isMobileScreen ? '3px 6px' : '6px 9px',
                        borderRadius: isMobileScreen ? '6px' : '10px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        zIndex: 10,
                      }}
                    >
                      <i style={{ width: '2.5px', height: '5px', borderRadius: '1px', background: '#7FB0FF' }} />
                      <i style={{ width: '2.5px', height: '10px', borderRadius: '1px', background: '#7FB0FF' }} />
                      <i style={{ width: '2.5px', height: '15px', borderRadius: '1px', background: '#7FB0FF' }} />
                      <i style={{ width: '2.5px', height: '8px', borderRadius: '1px', background: '#7FB0FF' }} />
                      <i style={{ width: '2.5px', height: '12px', borderRadius: '1px', background: '#7FB0FF' }} />
                      <i style={{ width: '2.5px', height: '6px', borderRadius: '1px', background: '#7FB0FF' }} />
                    </div>
                  )}
                </div>

                {/* Tile 2: Learner (Live Webcam & Microphone or Interactive Waveform) */}
                <div
                  className={`assessment-replay-tile lv-tile lv-you ${currentSpeaker === 'learner' && isPlaying ? 'lv-speaking' : ''}`}
                  style={{
                    position: 'relative',
                    aspectRatio: '16/9',
                    minHeight: isMobileScreen ? '96px' : (isFullPage ? '320px' : '160px'),
                    maxHeight: isMobileScreen && mobileFsView === 'split' ? '170px' : undefined,
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                    borderRadius: isMobileScreen ? '10px' : '16px',
                    overflow: 'hidden',
                    background: '#0F2350',
                    border: currentSpeaker === 'learner' && isPlaying
                      ? '2.5px solid #35D07F'
                      : '2px solid rgba(255, 255, 255, 0.08)',
                    boxShadow: currentSpeaker === 'learner' && isPlaying
                      ? '0 0 0 4px rgba(53, 208, 127, 0.2), 0 8px 24px rgba(53, 208, 127, 0.25)'
                      : '0 4px 16px rgba(0,0,0,0.3)',
                    transition: 'all 0.25s ease',
                  }}
                >
                  {/* Real Video Stream OR Learner Avatar/Placeholder taking full tile area */}
                  {resolvedUrl && !videoHasError && !isAudioOnly ? (
                    <video
                      ref={activeViewMode === 'MEET_GRID' ? videoRef : undefined}
                      src={resolvedUrl}
                      playsInline
                      onError={(err) => {
                        console.warn('[AssessmentRecordingReplayCard] Meet Grid video stream error, checking fallbacks:', err);
                        if (typeof window !== 'undefined' && attemptId) {
                          const localFallback = sessionStorage.getItem(`trainova_attempt_video_${attemptId}`);
                          if (localFallback && localFallback !== resolvedUrl) {
                            console.log('[AssessmentRecordingReplayCard] Recovering Meet Grid with local session video blob.');
                            setResolvedUrl(localFallback);
                            setVideoHasError(false);
                            return;
                          }
                          getSessionRecordingBlob(attemptId).then((blob) => {
                            if (blob && blob.size > 0) {
                              const idbUrl = registerBlobUrl(URL.createObjectURL(blob));
                              console.log('[AssessmentRecordingReplayCard] Recovering Meet Grid with IndexedDB blob.');
                              setResolvedUrl(idbUrl);
                              setVideoHasError(false);
                              setRecordingStatus('READY');
                              return;
                            }
                            setVideoHasError(true);
                          }).catch(() => setVideoHasError(true));
                          return;
                        }
                        setVideoHasError(true);
                      }}
                      onLoadedMetadata={(e) => {
                        registerSessionMetadata(e.currentTarget);
                      }}
                      onTimeUpdate={() => {
                        if (useSessionEngine) {
                          handleSessionTimeUpdate();
                          return;
                        }
                        if (activeViewMode === 'RAW_VIDEO' && videoRef.current) {
                          const cur = Math.round(videoRef.current.currentTime);
                          setCurrentTime(cur);
                          syncTranscriptToTime(cur);
                        }
                      }}
                      onEnded={handleMediaEnded}
                      onClick={togglePlay}
                      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', cursor: 'pointer' }}
                    />
                  ) : (
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'radial-gradient(circle at center, #1E293B 0%, #0F172A 100%)',
                      }}
                    >
                      <div
                        style={{
                          width: isMobileScreen ? '44px' : (isFullPage ? '96px' : '82px'),
                          height: isMobileScreen ? '44px' : (isFullPage ? '96px' : '82px'),
                          borderRadius: '50%',
                          background: '#0D4FBD',
                          border: currentSpeaker === 'learner' && isPlaying ? '3px solid #38BDF8' : '2px solid #1E3A8A',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: currentSpeaker === 'learner' && isPlaying ? '0 0 20px rgba(56, 189, 248, 0.5)' : 'none',
                          transform: currentSpeaker === 'learner' && isPlaying ? 'scale(1.05)' : 'scale(1)',
                          transition: 'all 0.2s ease',
                          fontSize: isMobileScreen ? '18px' : (isFullPage ? '34px' : '28px'),
                          color: '#FFFFFF',
                          fontWeight: 700,
                        }}
                      >
                        {learnerName ? learnerName.charAt(0).toUpperCase() : 'L'}
                      </div>

                      <span style={{ fontSize: isMobileScreen ? '10px' : '12px', color: currentSpeaker === 'learner' && isPlaying ? '#BAE6FD' : '#94A3B8', marginTop: isMobileScreen ? '6px' : '12px', fontWeight: currentSpeaker === 'learner' && isPlaying ? 700 : 500 }}>
                        {captureMode === 'AUDIO_VIDEO' || captureMode === 'VIDEO' ? 'Learner (Camera Active)' : isAudioOnly ? '🎙️ Audio Recording Active' : 'Learner'}
                      </span>
                    </div>
                  )}

                  {/* Top Speaker Tag */}
                  <div
                    style={{
                      position: 'absolute',
                      top: isMobileScreen ? '6px' : '12px',
                      left: isMobileScreen ? '6px' : '12px',
                      fontSize: isMobileScreen ? '9px' : '11px',
                      fontWeight: 700,
                      color: currentSpeaker === 'learner' && isPlaying ? '#D6FBE6' : '#38BDF8',
                      background: currentSpeaker === 'learner' && isPlaying ? 'rgba(53, 208, 127, 0.35)' : 'rgba(5, 15, 38, 0.8)',
                      backdropFilter: 'blur(8px)',
                      padding: isMobileScreen ? '2px 6px' : '4px 10px',
                      borderRadius: isMobileScreen ? '4px' : '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: isMobileScreen ? '4px' : '6px',
                      border: currentSpeaker === 'learner' && isPlaying ? '1px solid rgba(53, 208, 127, 0.5)' : '1px solid rgba(255, 255, 255, 0.08)',
                      zIndex: 10,
                      letterSpacing: '0.04em',
                    }}
                  >
                    <span
                      style={{
                        width: isMobileScreen ? '5px' : '7px',
                        height: isMobileScreen ? '5px' : '7px',
                        borderRadius: '50%',
                        background: currentSpeaker === 'learner' && isPlaying ? '#35D07F' : '#38BDF8',
                        animation: currentSpeaker === 'learner' && isPlaying ? 'pulse 1s infinite' : 'none',
                      }}
                    />
                    LEARNER · {learnerName}
                  </div>

                  {/* Live Audio Equalizer on Tile */}
                  <div
                    style={{
                      position: 'absolute',
                      top: isMobileScreen ? '6px' : '12px',
                      right: isMobileScreen ? '6px' : '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: isMobileScreen ? '1.5px' : '2.5px',
                      background: 'rgba(5, 15, 38, 0.8)',
                      backdropFilter: 'blur(8px)',
                      padding: isMobileScreen ? '2px 5px' : '4px 8px',
                      borderRadius: isMobileScreen ? '4px' : '6px',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      zIndex: 10,
                    }}
                  >
                    {[10, 18, 26, 14, 20].map((h, i) => (
                      <div
                        key={i}
                        style={{
                          width: isMobileScreen ? '2px' : '2.5px',
                          height: currentSpeaker === 'learner' && isPlaying
                            ? `${Math.max(3, Math.round((isMobileScreen ? h * 0.7 : h) * (Math.cos(currentTime * 4 + i) + 1.2)))}px`
                            : '3px',
                          background: currentSpeaker === 'learner' ? '#38BDF8' : '#64748B',
                          borderRadius: '1px',
                          transition: 'height 0.1s ease',
                        }}
                      />
                    ))}
                  </div>

                  {/* Bottom-Left Learner Tag matching Live Simulation */}
                  <div
                    className="lv-tag lv-learner-tag"
                    style={{
                      position: 'absolute',
                      left: isMobileScreen ? '6px' : '12px',
                      bottom: isMobileScreen ? '6px' : '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: isMobileScreen ? '4px' : '8px',
                      background: 'rgba(5, 15, 38, 0.85)',
                      backdropFilter: 'blur(8px)',
                      padding: isMobileScreen ? '3px 6px' : '6px 12px 6px 8px',
                      borderRadius: isMobileScreen ? '6px' : '10px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      zIndex: 10,
                    }}
                  >
                    <b style={{ color: '#FFFFFF', fontSize: isMobileScreen ? '11px' : '13px', fontWeight: 600 }}>You</b>
                    <span style={{ fontSize: isMobileScreen ? '10px' : '12px', color: '#9AABCB' }}>{learnerName}</span>
                  </div>

                  {/* Bottom-Right Learner Speaking Wave matching Live Simulation */}
                  {currentSpeaker === 'learner' && isPlaying && (
                    <div
                      className="lv-wave lv-g"
                      style={{
                        position: 'absolute',
                        right: isMobileScreen ? '6px' : '12px',
                        bottom: isMobileScreen ? '6px' : '12px',
                        display: 'flex',
                        gap: '2px',
                        alignItems: 'flex-end',
                        background: 'rgba(5, 15, 38, 0.85)',
                        backdropFilter: 'blur(8px)',
                        padding: isMobileScreen ? '3px 6px' : '6px 9px',
                        borderRadius: isMobileScreen ? '6px' : '10px',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        zIndex: 10,
                      }}
                    >
                      <i style={{ width: '2.5px', height: '5px', borderRadius: '1px', background: '#35D07F' }} />
                      <i style={{ width: '2.5px', height: '11px', borderRadius: '1px', background: '#35D07F' }} />
                      <i style={{ width: '2.5px', height: '15px', borderRadius: '1px', background: '#35D07F' }} />
                      <i style={{ width: '2.5px', height: '8px', borderRadius: '1px', background: '#35D07F' }} />
                      <i style={{ width: '2.5px', height: '12px', borderRadius: '1px', background: '#35D07F' }} />
                      <i style={{ width: '2.5px', height: '6px', borderRadius: '1px', background: '#35D07F' }} />
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Hidden audio element used when audio-only or video is unmounted */}
            {resolvedUrl && (
              <audio
                ref={audioMediaRef}
                src={resolvedUrl}
                preload="auto"
                playsInline
                style={{ display: 'none' }}
                onLoadedMetadata={(e) => {
                  registerSessionMetadata(e.currentTarget);
                }}
                onTimeUpdate={() => {
                  if (useSessionEngine) handleSessionTimeUpdate();
                }}
                onEnded={handleMediaEnded}
              />
            )}

            {/* Live Closed Caption Subtitle Strip */}
            {currentSubtitle && (
              <div
                style={{
                  padding: isMobileScreen ? '6px 10px' : '11px clamp(12px, 3vw, 20px)',
                  background: 'rgba(11, 17, 32, 0.95)',
                  backdropFilter: 'blur(8px)',
                  borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                  fontSize: isMobileScreen ? '11.5px' : '13.5px',
                  color: currentSpeaker === 'persona' ? '#FDE68A' : '#BAE6FD',
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobileScreen ? '6px' : '10px',
                  lineHeight: 1.4,
                  flexShrink: 0,
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  boxSizing: 'border-box',
                }}
              >
                <span
                  style={{
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    fontSize: isMobileScreen ? '9.5px' : '10.5px',
                    background: currentSpeaker === 'persona' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(13, 79, 189, 0.25)',
                    color: currentSpeaker === 'persona' ? '#F59E0B' : '#38BDF8',
                    border: currentSpeaker === 'persona' ? '1px solid rgba(245, 158, 11, 0.3)' : '1px solid rgba(56, 189, 248, 0.3)',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    letterSpacing: '0.04em',
                    flexShrink: 0,
                  }}
                >
                  {currentSpeaker === 'persona' ? `[${personaName}]` : `[${learnerName}]`}
                </span>
                <span style={{ fontStyle: 'italic', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#FFFFFF' }}>
                  "{currentSubtitle}"
                </span>
              </div>
            )}
          </div>

          {/* Playback Controls & Interactive Scrubber */}
          <div
            style={{
              padding: isMobileScreen ? '8px 10px' : '14px clamp(12px, 3vw, 20px)',
              background: '#0B1120',
              borderTop: '1px solid rgba(255, 255, 255, 0.08)',
              display: 'flex',
              alignItems: 'center',
              gap: isMobileScreen ? '6px' : '12px',
              flexWrap: 'wrap',
              flexShrink: 0,
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            {/* Play / Pause */}
            <button
              type="button"
              onClick={togglePlay}
              title={isPlaying ? 'Pause assessment replay' : 'Play assessment replay'}
              style={{
                background: '#0D4FBD',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                padding: isMobileScreen ? '6px 12px' : '8px 16px',
                fontSize: isMobileScreen ? '12px' : '13px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                boxShadow: '0 2px 10px rgba(13, 79, 189, 0.45)',
                transition: 'all 0.15s ease',
              }}
            >
              {isPlaying ? '⏸ Pause' : '▶ Play'}
            </button>

            {/* 10s Rewind & Forward */}
            <button
              type="button"
              onClick={handleRewind10}
              title="Rewind 10 seconds"
              style={{ background: '#162032', color: '#CBD5E1', border: '1px solid #2B3A55', borderRadius: '6px', padding: isMobileScreen ? '5px 8px' : '6px 11px', fontSize: isMobileScreen ? '11px' : '11.5px', fontWeight: 600, cursor: 'pointer' }}
            >
              ⏪ 10s
            </button>
            <button
              type="button"
              onClick={handleForward10}
              title="Skip forward 10 seconds"
              style={{ background: '#162032', color: '#CBD5E1', border: '1px solid #2B3A55', borderRadius: '6px', padding: isMobileScreen ? '5px 8px' : '6px 11px', fontSize: isMobileScreen ? '11px' : '11.5px', fontWeight: 600, cursor: 'pointer' }}
            >
              ⏩ 10s
            </button>

            {/* Continuous Loop Toggle */}
            <button
              type="button"
              onClick={() => setIsLooping(!isLooping)}
              title={isLooping ? 'Continuous looping is active' : 'Click to enable continuous looping'}
              style={{
                background: isLooping ? 'rgba(13, 79, 189, 0.25)' : '#162032',
                color: isLooping ? '#38BDF8' : '#94A3B8',
                border: isLooping ? '1px solid rgba(56, 189, 248, 0.4)' : '1px solid #2B3A55',
                borderRadius: '6px',
                padding: isMobileScreen ? '5px 8px' : '6px 10px',
                fontSize: isMobileScreen ? '11px' : '11.5px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.15s ease',
              }}
            >
              <span>🔁</span>
              <span>{isLooping ? (isMobileScreen ? 'Loop' : 'Loop ON') : (isMobileScreen ? 'No loop' : 'Loop OFF')}</span>
            </button>

            {/* Scrubber Timeline: Full-width on mobile */}
            <div
              style={{
                flex: isMobileScreen ? '0 0 100%' : '1 1 180px',
                width: isMobileScreen ? '100%' : undefined,
                order: isMobileScreen ? -1 : 0,
                marginBottom: isMobileScreen ? '4px' : '0',
                minWidth: isMobileScreen ? '100%' : '120px',
                maxWidth: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span style={{ fontSize: isMobileScreen ? '11px' : '12px', fontWeight: 600, color: '#94A3B8', fontFamily: 'monospace', minWidth: '38px' }}>
                {formatTime(currentTime)}
              </span>
              <div
                style={{
                  flex: 1,
                  height: '6px',
                  background: '#1E293B',
                  borderRadius: '3px',
                  cursor: 'pointer',
                  position: 'relative',
                  touchAction: 'none',
                }}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const clickX = e.clientX - rect.left;
                  const ratio = Math.max(0, Math.min(1, clickX / rect.width));
                  const targetSec = Math.round(ratio * totalDurationSec);
                  handleSeek(targetSec);
                }}
              >
                <div style={{ width: `${(currentTime / totalDurationSec) * 100}%`, height: '100%', background: '#2F7BFF', borderRadius: '3px' }} />
                {/* Markers for transcript turns */}
                {cleanTurns.map((turn, i) => {
                  const isActive = activeTurnIndex === i;
                  return (
                    <div
                      key={turn.id || i}
                      title={`Turn #${turn.turnNumber}: ${turn.speakerName} (${formatTime(turn.turnSec)})\n${turn.text.slice(0, 60)}...`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTranscriptTurnClick(i);
                      }}
                      style={{
                        position: 'absolute',
                        left: `${(turn.turnSec / totalDurationSec) * 100}%`,
                        top: '-3px',
                        width: isMobileScreen ? '10px' : '12px',
                        height: isMobileScreen ? '10px' : '12px',
                        borderRadius: '50%',
                        background: isActive ? '#FFFFFF' : (turn.isLearner ? '#2F7BFF' : '#F59E0B'),
                        border: isActive
                          ? (turn.isLearner ? '2px solid #2F7BFF' : '2px solid #F59E0B')
                          : '1.5px solid #0B1120',
                        boxShadow: isActive
                          ? (turn.isLearner ? '0 0 10px #2F7BFF' : '0 0 10px #F59E0B')
                          : 'none',
                        cursor: 'pointer',
                        zIndex: isActive ? 4 : 2,
                        transform: isActive ? 'scale(1.2)' : 'scale(1)',
                        transition: 'all 0.15s ease',
                      }}
                    />
                  );
                })}
              </div>
              <span style={{ fontSize: isMobileScreen ? '11px' : '12px', fontWeight: 600, color: '#64748B', fontFamily: 'monospace', minWidth: '38px' }}>
                {formatTime(totalDurationSec)}
              </span>
            </div>

            {/* Playback Speed: Cycling single button on mobile, group on desktop */}
            {isMobileScreen ? (
              <button
                type="button"
                onClick={cycleSpeed}
                title="Tap to change playback speed"
                style={{
                  padding: '5px 8px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 700,
                  background: playbackSpeed !== 1 ? '#0D4FBD' : '#162032',
                  color: playbackSpeed !== 1 ? '#FFFFFF' : '#94A3B8',
                  border: '1px solid #2B3A55',
                  cursor: 'pointer',
                }}
              >
                ⚡ {playbackSpeed}x
              </button>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                {[1, 1.25, 1.5, 2].map((spd) => (
                  <button
                    key={spd}
                    type="button"
                    onClick={() => handleSpeedChange(spd)}
                    style={{
                      padding: '4px 8px',
                      borderRadius: '5px',
                      fontSize: '11px',
                      fontWeight: playbackSpeed === spd ? 700 : 500,
                      background: playbackSpeed === spd ? '#0D4FBD' : '#162032',
                      color: playbackSpeed === spd ? '#FFFFFF' : '#94A3B8',
                      border: '1px solid #2B3A55',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {spd}x
                  </button>
                ))}
              </div>
            )}

            {/* Download Recording Button */}
            {resolvedUrl && (
              <a
                href={resolvedUrl}
                download={`trainova_assessment_${attemptId}.webm`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: '#162032',
                  border: '1px solid #2B3A55',
                  borderRadius: '6px',
                  padding: isMobileScreen ? '5px 8px' : '6px 11px',
                  color: '#38BDF8',
                  textDecoration: 'none',
                  fontSize: isMobileScreen ? '11px' : '11.5px',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  cursor: 'pointer',
                  transition: 'background 0.15s ease',
                }}
                title="Download full assessment session recording"
              >
                ⬇️{isMobileScreen ? '' : ' Download'}
              </a>
            )}

            {/* Audio Mute Toggle */}
            <button
              type="button"
              onClick={toggleMute}
              style={{
                background: '#162032',
                border: '1px solid #2B3A55',
                borderRadius: '6px',
                padding: isMobileScreen ? '5px 8px' : '6px 10px',
                color: isMuted ? '#EF4444' : '#38BDF8',
                cursor: 'pointer',
                fontSize: isMobileScreen ? '11px' : '12px',
                fontWeight: 600,
              }}
              title={isMuted ? 'Unmute Audio' : 'Mute Audio'}
            >
              {isMuted ? (isMobileScreen ? '🔇' : '🔇 Muted') : (isMobileScreen ? '🔊' : '🔊 Audio ON')}
            </button>
          </div>

          {/* When in mobile portrait full screen, render transcript inside vertical column */}
          {isFullPage && isMobileScreen && !isLandscape && mobileFsView !== 'video' && renderTranscriptPanel(false, true)}

          {/* When NOT in full screen, render the synchronized transcript panel underneath */}
          {!isFullPage && renderTranscriptPanel(false, false)}
        </div>

        {/* Right Side: Synchronized Transcript in Full Screen Theater Mode (Desktop & Mobile Landscape) */}
        {isFullPage && (!isMobileScreen || isLandscape) && (
          <div
            style={isMobileScreen ? {
              flex: '0 0 42%',
              width: '42%',
              maxWidth: '42%',
              minWidth: 0,
              borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
              background: '#070C18',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              overflow: 'hidden',
            } : {
              width: '380px',
              maxWidth: '38%',
              borderLeft: '1px solid rgba(255, 255, 255, 0.08)',
              background: '#070C18',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              minWidth: '320px',
            }}
          >
            {renderTranscriptPanel(true)}
          </div>
        )}
      </div>
    </div>
  );
};
